import {
  Component, OnInit, OnDestroy, AfterViewInit,
  ViewChild, ElementRef, HostListener, Inject
} from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { ChangeDetectorRef } from '@angular/core';

// TipTap imports
import { Editor, Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table';
import { TableHeader } from '@tiptap/extension-table';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, Color, FontSize } from '@tiptap/extension-text-style';
import { Node as TiptapNode, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey,TextSelection} from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { ConfirmPlanComponent } from 'src/app/confirm-plan/confirm-plan.component';

if (typeof (Promise as any).withResolvers !== 'function') {
  (Promise as any).withResolvers = function withResolvers<T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: any) => void;
    const promise = new Promise<T>((res, rej) => { resolve = res; reject = rej; });
    return { promise, resolve, reject };
  };
}

import * as pdfjsLib from 'pdfjs-dist';
(pdfjsLib as any).GlobalWorkerOptions.workerSrc =
  new URL('assets/pdf.worker.min.mjs', document.baseURI).href;

// Services / utils
import { HttpService } from 'src/app/services/http.service';
import { URLUtils } from 'src/app/urlUtils';
import { DocumentService } from '../document/document.service';
import { ModalService } from 'src/app/model/model.service';
import { RandomService } from '../services/random.service';
import { SidebarService } from '../services/sidebar.service';
import { ConfirmationDialogService } from 'src/app/confirmation-dialog/confirmation-dialog.service';
import { ConfirmDialogComponent } from 'src/app/confirm-dialog/confirm-dialog.component';
import { environment } from 'src/environments/environment';
import { ensureActiveSubscription } from '../shared/subscription-dialog';
import { PAGE_CONTENT_HEIGHT_PX, applyDocLayoutCssVars } from './doc-layout';

// ─── Custom TipTap Nodes ─────────────────────────────────────────────────────

const OverviewNode = TiptapNode.create({
  name: 'overview',
  group: 'block',
  content: 'inline*',
  parseHTML() { return [{ tag: 'div[data-type="overview"]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'overview', class: 'tiptap-overview' }), 0];
  },
});

const PageBreakNode = TiptapNode.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  parseHTML() { return [{ tag: 'div[data-type="page-break"]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-type': 'page-break',
      class: 'tiptap-page-break',
      contenteditable: 'false'
    })];
  },
});

// Google-Docs-style "Title" block — a distinct, oversized document title that
// lives at the top of the body. Rendered as an h1 with a data-type marker so it
// is never confused with a Section heading (plain h1) by the outline/sidebar or
// the LaTeX backend (see html_to_latex in the Python API).
const TitleNode = TiptapNode.create({
  name: 'docTitle',
  group: 'block',
  content: 'inline*',
  defining: true,
  addAttributes() {
    return {
      // Title alignment lives on the node itself instead of the shared TextAlign
      // global attribute. TextAlign suppresses the inline style whenever the
      // value equals its global default ('left'), so a title the user aligned
      // left would emit no style and fall back to the centered CSS default —
      // the "the title is always centered" bug. Rendering our own explicit
      // text-align for every value (including left) lets the user pick any
      // alignment and have it stick.
      align: {
        default: 'center',
        parseHTML: (el: HTMLElement) => el.style.textAlign || 'center',
        renderHTML: (attrs: any) => ({ style: `text-align: ${attrs['align'] || 'center'}` }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'h1[data-type="doc-title"]', priority: 100 }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['h1', mergeAttributes(HTMLAttributes, { 'data-type': 'doc-title', class: 'tiptap-doc-title' }), 0];
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        if (!this.editor.isActive('docTitle')) return false;
        return this.editor.chain()
          .splitBlock()
          .setNode('paragraph')
          .setTextAlign('left')
          .run();
      },
    };
  },
});

// ─── Auto-capitalize headings (Google-Docs-style) ────────────────────────────
// The first letter of a Title / Section / Sub Section is always upper-cased,
// live as you type — typing a lowercase first character immediately flips it to
// uppercase, and converting a paragraph into a heading capitalizes it too.
// Normal paragraph text is never touched. Implemented as an appendTransaction so
// the change rides along with the user's own edit (single undo step, caret
// preserved) instead of firing a competing transaction from onUpdate.
const HEADING_CAP_TYPES = new Set(['heading', 'docTitle']);
const autoCapitalizePluginKey = new PluginKey('autoCapitalizeHeadings');

const AutoCapitalizeHeadings = Extension.create({
  name: 'autoCapitalizeHeadings',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: autoCapitalizePluginKey,
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((t) => t.docChanged)) return null;
          let tr: any = null;
          newState.doc.descendants((node: any, pos: number) => {
            if (!HEADING_CAP_TYPES.has(node.type.name) || node.content.size === 0) return;
            const from = pos + 1; // first position inside the block's content
            const first = newState.doc.textBetween(from, from + 1);
            if (!first) return; // first inline child isn't text (nothing to capitalize)
            const upper = first.toUpperCase();
            if (upper === first) return; // already uppercase, or a non-letter (digit/quote/…)
            if (!tr) tr = newState.tr;
            // Preserve the first character's marks (e.g. a bold title) instead of
            // dropping them, which a bare insertText() would do.
            const marks = node.firstChild?.marks ?? [];
            tr.replaceWith(from, from + 1, newState.schema.text(upper, marks));
          });
          return tr;
        },
      }),
    ];
  },
});

// ─── Heading auto-numbering ──────────────────────────────────────────────────
// Sections (h1) / Sub Sections (h2) / Sub-sub-sections (h3) are numbered live,
// mirroring the compiled PDF's \section/\subsection/\subsubsection numbering.
// The number is attached as a `data-num` node decoration (view-only, never part
// of the saved HTML — see the CSS ::before rules that render attr(data-num)), so
// like the old CSS counters it can't be selected, typed into or deleted.
//
// Unlike a plain CSS counter it can OMIT empty parent levels: a Sub Section with
// no preceding Section is numbered "1", not "0.1" (there is no Section 0). The
// same "drop leading empty levels" rule is applied to the compiled PDF via
// \thesubsection / \thesubsubsection in the LaTeX preamble (app.py), so the
// editor and the PDF stay in lock-step.
const headingNumberingKey = new PluginKey('headingNumbering');

const HeadingNumbering = Extension.create({
  name: 'headingNumbering',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: headingNumberingKey,
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            let section = 0, subsection = 0, subsubsection = 0;
            state.doc.descendants((node: any, pos: number) => {
              if (node.type.name !== 'heading') return;
              const level = node.attrs['level'];
              let num = '';
              if (level === 1) {
                section++; subsection = 0; subsubsection = 0;
                num = `${section}`;
              } else if (level === 2) {
                subsection++; subsubsection = 0;
                num = section > 0 ? `${section}.${subsection}` : `${subsection}`;
              } else if (level === 3) {
                subsubsection++;
                const parts: number[] = [];
                if (section > 0) parts.push(section);
                if (subsection > 0) parts.push(subsection);
                parts.push(subsubsection);
                num = parts.join('.');
              } else {
                return;
              }
              decorations.push(
                Decoration.node(pos, pos + node.nodeSize, { 'data-num': num }),
              );
            });
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

// ─── List continuity: join adjacent lists + continuous ordered-list numbering ──
// Two separate edits break numbered-list continuity, and this one appendTransaction
// fixes both after every change:
//   (a) Deleting a list item can leave two ADJACENT sibling <ol>/<ul> nodes where
//       there was one; ProseMirror doesn't merge them, so the second restarts at 1.
//       → we join any adjacent same-type lists back into one.
//   (b) Deleting a middle item (or exiting a list) can leave an EMPTY paragraph
//       wedged between the two halves — NOT adjacent, so it can't be joined, and
//       the second <ol> still restarts at 1. This is the exact bug in the user's
//       screenshots: remove item 2 and item 3 shows "1" instead of "2".
//       → we recompute each ordered list's `start` so a list separated from a
//         preceding ordered list by ONLY empty paragraphs keeps counting
//         (1 → 2 → …), exactly like Google Docs. Empty paragraphs are transparent
//         to the run; any REAL content (text, heading, table, bullet list, page
//         break) ends the run so the next ordered list correctly starts back at 1.
const joinAdjacentListsKey = new PluginKey('joinAdjacentLists');
const LIST_TYPE_NAMES = new Set(['orderedList', 'bulletList']);

const JoinAdjacentLists = Extension.create({
  name: 'joinAdjacentLists',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: joinAdjacentListsKey,
        appendTransaction: (transactions: readonly any[], _oldState: any, newState: any) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;

          const tr = newState.tr;

          // ── (a) Join adjacent same-type lists ──────────────────────────────
          // `contentStart` is the position of a node's first child (0 for the doc
          // root, nodeStart+1 for any other node).
          const boundaries: number[] = [];
          const walkJoin = (node: any, contentStart: number) => {
            let childStart = contentStart;
            for (let i = 0; i < node.childCount; i++) {
              const child = node.child(i);
              if (i > 0) {
                const prev = node.child(i - 1);
                if (LIST_TYPE_NAMES.has(child.type.name) && child.type === prev.type) {
                  boundaries.push(childStart);
                }
              }
              walkJoin(child, childStart + 1);
              childStart += child.nodeSize;
            }
          };
          walkJoin(newState.doc, 0);
          // Join from the last boundary backwards so each join never shifts the
          // positions of the boundaries still to be processed.
          boundaries.sort((a, b) => b - a).forEach((pos) => {
            try { tr.join(pos); } catch { /* not joinable — skip */ }
          });

          // ── (b) Continuous ordered-list numbering across empty-paragraph gaps ─
          // Walk the (possibly just-joined) doc; at every level keep a running
          // total for the current continuous run of ordered lists. A list's
          // `start` should be (previous run total + 1) while the run is alive,
          // else 1. setNodeMarkup only changes attrs (node size unchanged), so
          // every position collected from this single tr.doc snapshot stays valid.
          const isEmptyPara = (n: any) =>
            n.type.name === 'paragraph' && n.content.size === 0;
          const fixes: { pos: number; attrs: any; start: number }[] = [];
          const walkNum = (node: any, contentStart: number) => {
            let childStart = contentStart;
            let runTotal = 0;       // highest number used so far in the live run
            let runActive = false;  // are we inside a continuous ordered-list run?
            for (let i = 0; i < node.childCount; i++) {
              const child = node.child(i);
              const name = child.type.name;
              if (name === 'orderedList') {
                const desired = runActive ? runTotal + 1 : 1;
                if ((child.attrs.start || 1) !== desired) {
                  fixes.push({ pos: childStart, attrs: child.attrs, start: desired });
                }
                runTotal = desired - 1 + child.childCount;
                runActive = true;
              } else if (!isEmptyPara(child)) {
                // Any non-empty, non-ordered-list block ends the run; an empty
                // paragraph is transparent and keeps the run alive.
                runActive = false;
                runTotal = 0;
              }
              walkNum(child, childStart + 1);
              childStart += child.nodeSize;
            }
          };
          walkNum(tr.doc, 0);
          fixes.forEach(({ pos, attrs, start }) => {
            try { tr.setNodeMarkup(pos, undefined, { ...attrs, start }); }
            catch { /* node moved — skip */ }
          });

          return tr.docChanged ? tr : null;
        },
      }),
    ];
  },
});

// ─── Indentation Extension (Google-Docs-style Tab) ───────────────────────────
// Tab increases the block indent by one step; Shift+Tab decreases it. Inside a
// list, Tab/Shift+Tab nest/un-nest the list item instead (exactly like Docs).
const INDENT_STEP_PX = 40;
const MAX_INDENT_LEVEL = 12;

const IndentExtension = Extension.create({
  name: 'indent',

  addOptions() {
    return { types: ['paragraph', 'heading'] as string[] };
  },

  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        indent: {
          default: 0,
          parseHTML: (element: HTMLElement) => {
            const ml = parseInt(element.style.marginLeft || '0', 10);
            return ml ? Math.round(ml / INDENT_STEP_PX) : 0;
          },
          renderHTML: (attributes: any) => {
            if (!attributes['indent']) return {};
            return { style: `margin-left: ${attributes['indent'] * INDENT_STEP_PX}px` };
          },
        },
      },
    }];
  },

  addCommands() {
    const applyIndent = (direction: number) => ({ state, dispatch }: any) => {
      const { from, to } = state.selection;
      let tr = state.tr;
      let changed = false;
      state.doc.nodesBetween(from, to, (node: any, pos: number) => {
        if (this.options.types.includes(node.type.name)) {
          const current = node.attrs.indent || 0;
          const next = Math.min(MAX_INDENT_LEVEL, Math.max(0, current + direction));
          if (next !== current) {
            tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
            changed = true;
          }
        }
      });
      if (changed && dispatch) dispatch(tr);
      return changed;
    };
    return {
      indent: () => applyIndent(1),
      outdent: () => applyIndent(-1),
    } as any;
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        // Inside a table, Tab must move the caret to the next cell — NOT indent
        // the cell or drag the typed text along with it. Our own Tab handler used
        // to win over the Table extension's, so tables ran `indent()`, which
        // carried the cell text and eventually corrupted the table (text vanished,
        // preview failed). Route table Tab to proper cell navigation instead.
        if (this.editor.isActive('table')) {
          this.editor.commands.goToNextCell();
          return true;
        }
        if (this.editor.isActive('listItem')) {
          this.editor.commands.sinkListItem('listItem');
        } else {
          (this.editor.commands as any).indent();
        }
        return true; // always consume Tab so focus never leaves the editor
      },
      'Shift-Tab': () => {
        if (this.editor.isActive('table')) {
          this.editor.commands.goToPreviousCell();
          return true;
        }
        if (this.editor.isActive('listItem')) {
          this.editor.commands.liftListItem('listItem');
        } else {
          (this.editor.commands as any).outdent();
        }
        return true;
      },
      // After a Section / Sub Section heading, Enter starts a normal paragraph
      // rather than another heading — otherwise the body text typed under a
      // subsection is styled (and numbered) as another heading instead of the
      // subsection's content.
      Enter: () => {
        if (!this.editor.isActive('heading')) return false;
        const { $from, empty } = this.editor.state.selection;
        const atEnd = empty && $from.parentOffset === $from.parent.content.size;
        if (!atEnd) return false;
        return this.editor.chain().splitBlock().setNode('paragraph').run();
      },
      // Google-Docs behaviour: Backspace with the caret at the very start of an
      // indented block removes one indent step instead of merging the block into
      // the previous one. Without this, once you Tab a line you can't Backspace
      // to undo the indent — the caret is already at offset 0, so the default
      // Backspace joins blocks and the indent (and apparent text position) sticks.
      Backspace: () => {
        const { empty, $from } = this.editor.state.selection;
        if (!empty || $from.parentOffset !== 0) return false; // let normal delete run
        if (($from.parent.attrs['indent'] || 0) > 0) {
          (this.editor.commands as any).outdent();
          return true;
        }
        return false;
      },
    };
  },
});

// ─── Pagination Extension ─────────────────────────────────────────────────────
// Same algorithm as the old version: sum each block's own height (no margin
// collapsing), and when a block would overflow the page, insert a spacer just
// tall enough to push the next block onto a fresh page.
const PAGE_SEAM_GAP_PX = 8;

const paginationPluginKey = new PluginKey('pagination');

function createPaginationExtension() {
  return Extension.create({
    name: 'pagination',

    addProseMirrorPlugins() {
      let scheduled: any = null;

// Top-level block tags that may be split ACROSS a page boundary (like Google
// Docs) rather than pushed whole to the next page. Only flowing text blocks are
// safe to split by inserting a seam between two of their line boxes; tables,
// images, lists and page-breaks stay atomic.
const SPLITTABLE_BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3']);

// Build the fixed-height gray seam element the pagination inserts to fill the
// unused remainder of a page. `isSplit` seams sit INSIDE a paragraph (between
// two line boxes), so they must be display:block to force the line break.
const makeSeamEl = (spacerHeight: number, isManual: boolean, isSplit: boolean): HTMLElement => {
  const seam = document.createElement('div');
  seam.className = isManual ? 'tiptap-page-seam tiptap-page-seam--manual' : 'tiptap-page-seam';
  if (isSplit) seam.style.display = 'block';
  seam.style.height = `${spacerHeight}px`;
  seam.setAttribute('contenteditable', 'false');
  return seam;
};

// Build a spacer TABLE ROW the pagination inserts BETWEEN two real rows to push
// the overflowing rows onto the next page — the editor equivalent of the PDF's
// breakable longtable (which splits a tall table across pages). It must be a
// <tr> (not a <div>): the browser relocates non-row children out of a <tbody>,
// so only a real row can create vertical space inside a table. It's tagged
// `tiptap-table-seam-row` so the next measurement pass skips it (keeping row
// heights seam-free → a stable decoration set, exactly like the text-line seams).
const makeTableSeamRow = (spacerHeight: number, colCount: number): HTMLElement => {
  const tr = document.createElement('tr');
  tr.className = 'tiptap-table-seam-row';
  tr.setAttribute('contenteditable', 'false');
  const td = document.createElement('td');
  td.colSpan = Math.max(1, colCount);
  td.style.height = `${spacerHeight}px`;
  td.style.padding = '0';
  td.style.border = 'none';
  td.style.background = 'transparent';
  tr.appendChild(td);
  return tr;
};

// Per-line rectangles of a block's TEXT, excluding any seam widgets (they carry
// no text, so a text-node walk never sees them). Used to find the exact line a
// page boundary falls on so we can break there. Heights are seam-free, which is
// what keeps the decoration set stable across recompute passes: a seam inserted
// on the previous pass shifts later lines down but never changes their heights,
// so the same boundary line — and the same document position — is chosen again.
//
// CRITICAL: each line's `height` is the block's LINE-BOX height (its computed
// line-height, e.g. 24px at 12pt × 1.5), NOT the glyph run's own rect height.
// Range.getClientRects() returns the tight glyph box (~16px = the 12pt em), which
// OMITS the leading (the 8px of line-height above+below the glyphs). Summing the
// glyph heights undercounted every line by a third, so runningHeight only ever
// reached ~66% of the real rendered height — the editor then fit ~1.5 real pages
// before drawing a "page" break and drifted badly from the compiled PDF (which
// paginates on true baselines). Using the computed line-height per line makes the
// sum equal the real rendered text height, so editor and PDF break on the same
// line. line-height is constant regardless of any seam, so heights stay seam-free
// (stable across recompute passes) exactly as before.
const getBlockLineRects = (blockEl: HTMLElement): { top: number; height: number; left: number }[] => {
  const cs = getComputedStyle(blockEl);
  let lineHeightPx = parseFloat(cs.lineHeight);
  // getComputedStyle returns 'normal' (→ NaN) when line-height isn't set in px;
  // approximate it from the font size (the editor sets 1.5 explicitly, so this is
  // only a defensive fallback).
  if (!isFinite(lineHeightPx) || lineHeightPx <= 0) {
    lineHeightPx = (parseFloat(cs.fontSize) || 16) * 1.2;
  }
  const lines: { top: number; height: number; left: number }[] = [];
  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const range = document.createRange();
    range.selectNodeContents(node);
    for (const r of Array.from(range.getClientRects())) {
      if (r.height === 0 || r.width === 0) continue;
      // Detect DISTINCT visual lines by their glyph `top`; the height we record
      // for each is the full line box (leading included), not the glyph rect.
      const existing = lines.find((l) => Math.abs(l.top - r.top) < 2);
      if (existing) {
        existing.left = Math.min(existing.left, r.left);
      } else {
        lines.push({ top: r.top, height: lineHeightPx, left: r.left });
      }
    }
    node = walker.nextNode();
  }
  lines.sort((a, b) => a.top - b.top);
  return lines;
};

// Collapsed vertical gap that renders ABOVE `el` between it and its previous
// sibling block — max(prev.margin-bottom, el.margin-top), the CSS margin-collapse
// rule. The pagination sum previously ignored inter-block margins entirely, but
// the compiled PDF spends real vertical space on them (\parskip between
// paragraphs, the \section skips, a table's 0.8em margins), so leaving them out
// was a second, smaller source of editor↔PDF page-break drift on top of the
// line-height bug. Margins are constant per block, so counting them keeps the
// height sum seam-free/stable.
const blockOuterMargins = (el: HTMLElement): { top: number; bottom: number } => {
  const cs = getComputedStyle(el);
  return { top: parseFloat(cs.marginTop) || 0, bottom: parseFloat(cs.marginBottom) || 0 };
};

const buildDecorations = (view: any): DecorationSet => {
  const dom = view.dom as HTMLElement;
  const decorations: Decoration[] = [];
  let runningHeight = 0;

  // Seam BEFORE a whole top-level block (atomic blocks + manual breaks).
  const pushSeam = (beforeEl: HTMLElement, spacerHeight: number, isManual: boolean) => {
    try {
      const pos = view.posAtDOM(beforeEl, 0);
      decorations.push(
        Decoration.widget(pos, () => makeSeamEl(spacerHeight, isManual, false),
          { side: -1, key: `seam-${pos}` })
      );
    } catch {
      // posAtDOM can occasionally fail mid-update — skip
    }
  };

  // Seam INSIDE a paragraph, before the line that would overflow the page — the
  // paragraph keeps flowing onto the next page instead of jumping whole (the
  // Google-Docs behaviour). The seam is a decoration, so the underlying text
  // stays one contiguous node: selection and editing are unaffected.
  const pushMidBlockSeam = (pos: number, spacerHeight: number) => {
    decorations.push(
      Decoration.widget(pos, () => makeSeamEl(spacerHeight, false, true),
        { side: -1, key: `seam-mid-${pos}` })
    );
  };

  // Spacer ROW inside a table, before the row that would overflow the page — the
  // table keeps flowing onto the next page (Google-Docs / PDF longtable behaviour)
  // instead of the whole table jumping or overflowing. A widget decoration, so the
  // underlying table node is untouched (editing/selection/save/PDF unaffected).
  const pushTableRowSeam = (pos: number, spacerHeight: number, colCount: number) => {
    decorations.push(
      Decoration.widget(pos, () => makeTableSeamRow(spacerHeight, colCount),
        { side: -1, key: `seam-row-${pos}` })
    );
  };

  // Document position immediately BEFORE the tableRow node that owns `tr`, so a
  // spacer row can be inserted there. posAtDOM lands somewhere inside the row;
  // resolving it up to the tableRow ancestor gives the exact node boundary.
  const tableRowStartPos = (trEl: HTMLElement): number | null => {
    try {
      const raw = view.posAtDOM(trEl, 0);
      const $pos = view.state.doc.resolve(raw);
      for (let d = $pos.depth; d >= 0; d--) {
        if ($pos.node(d).type.name === 'tableRow') return $pos.before(d);
      }
    } catch { /* posAtDOM can fail mid-update — skip */ }
    return null;
  };

  const children = Array.from(dom.children) as HTMLElement[];

  // Google-Docs-like: a manual page break is shown as a slim, fixed-height
  // marker right at the cursor — NOT the full remainder of the current page.
  // The compiled PDF still gets a real \newpage (see htmlToLatex), so this is
  // purely an editor-display simplification; automatic overflow breaks below
  // still render the true remainder, since those need to show real page
  // boundaries as content actually fills a page.
  const MANUAL_BREAK_MARKER_HEIGHT_PX = 28;

  children.forEach((child) => {
    // Seam widgets from the previous pass are page furniture, not content.
    if (child.classList.contains('tiptap-page-seam')) return;

    const isExplicitBreak = child.getAttribute('data-type') === 'page-break';
    if (isExplicitBreak) {
      pushSeam(child, MANUAL_BREAK_MARKER_HEIGHT_PX, true);
      runningHeight = 0;
      return;
    }

    // Tables split ACROSS pages (like the compiled PDF's longtable) instead of
    // being pushed whole / overflowing a page. TipTap renders a table inside a
    // `.tableWrapper` NodeView, so reach through to the <table> and paginate it
    // row by row: fill the current page, then drop a spacer row before whichever
    // row first crosses the page boundary so the rest of the table continues on
    // the next page. Adding rows to a full table now flows onto the next page.
    let tableEl: HTMLTableElement | null = null;
    if (child.tagName === 'TABLE') tableEl = child as HTMLTableElement;
    else if (child.classList.contains('tableWrapper')) tableEl = child.querySelector('table');

    if (tableEl) {
      // Skip spacer rows from a previous pass so heights stay seam-free (stable).
      const rows = Array.from(tableEl.rows).filter(
        (r) => !r.classList.contains('tiptap-table-seam-row')
      );
      const colCount = rows.length ? Math.max(...rows.map((r) => r.cells.length)) : 1;
      rows.forEach((tr, idx) => {
        const rh = tr.getBoundingClientRect().height;
        if (runningHeight + rh > PAGE_CONTENT_HEIGHT_PX && runningHeight > 0) {
          const spacerHeight = Math.max(0, PAGE_CONTENT_HEIGHT_PX - runningHeight) + PAGE_SEAM_GAP_PX;
          if (idx === 0) {
            // Overflow before the first row → move the whole table to the next
            // page (a seam BEFORE the table), so it doesn't open with an empty
            // gap above its first row.
            pushSeam(child, spacerHeight, false);
          } else {
            const rowPos = tableRowStartPos(tr);
            if (rowPos != null) pushTableRowSeam(rowPos, spacerHeight, colCount);
          }
          runningHeight = 0;
        }
        runningHeight += rh;
      });
      return;
    }

    const lines = SPLITTABLE_BLOCK_TAGS.has(child.tagName) ? getBlockLineRects(child) : [];

    if (lines.length > 1) {
      // Multi-line text block: fill the current page line by line, dropping a
      // seam before whichever line first crosses the page boundary, then keep
      // going (a block taller than a whole page yields several seams).
      for (const line of lines) {
        if (runningHeight + line.height > PAGE_CONTENT_HEIGHT_PX && runningHeight > 0) {
          const coords = view.posAtCoords({ left: line.left + 1, top: line.top + line.height / 2 });
          if (coords) {
            const spacerHeight = Math.max(0, PAGE_CONTENT_HEIGHT_PX - runningHeight) + PAGE_SEAM_GAP_PX;
            pushMidBlockSeam(coords.pos, spacerHeight);
            runningHeight = 0;
          }
        }
        runningHeight += line.height;
      }
      return;
    }

    // Atomic block (table, image, empty/one-line paragraph): move it whole.
    const blockHeight = lines.length === 1 ? lines[0].height : child.getBoundingClientRect().height;
    if (runningHeight + blockHeight > PAGE_CONTENT_HEIGHT_PX && runningHeight > 0) {
      const spacerHeight = Math.max(0, PAGE_CONTENT_HEIGHT_PX - runningHeight) + PAGE_SEAM_GAP_PX;
      pushSeam(child, spacerHeight, false);
      runningHeight = blockHeight;
    } else {
      runningHeight += blockHeight;
    }
  });

  return DecorationSet.create(view.state.doc, decorations);
};

      return [
        new Plugin({
          key: paginationPluginKey,

          state: {
            init(_, { doc }) {
              return DecorationSet.empty;
            },
            apply(tr, old, _oldState, newState) {
              const meta = tr.getMeta(paginationPluginKey);
              if (meta && meta.decorations) {
                return meta.decorations;
              }
              return old.map(tr.mapping, newState.doc);
            },
          },

          props: {
            decorations(state) {
              return this.getState(state);
            },
          },

          view(view) {
            const recompute = () => {
              const decorations = buildDecorations(view);
              view.dispatch(
                view.state.tr.setMeta(paginationPluginKey, { decorations })
              );
            };

            const scheduleRecompute = () => {
              if (scheduled) clearTimeout(scheduled);
              scheduled = setTimeout(recompute, 120);
            };

            setTimeout(recompute, 0);

            // The page canvas renders in Nimbus Roman (a webfont). Until it
            // loads the browser measures with a fallback font, so line wrapping —
            // and therefore every page break — is computed wrong. Recompute once
            // the real font is ready so the editor matches the PDF.
            try {
              (document as any).fonts?.ready?.then(() => scheduleRecompute());
              (document as any).fonts?.load?.('12pt "Nimbus Roman"')?.then(() => scheduleRecompute());
            } catch { /* Font Loading API unavailable — the initial pass stands */ }

            const stableAncestor = view.dom.parentElement?.parentElement;
            const resizeObserver = new ResizeObserver(() => scheduleRecompute());
            if (stableAncestor) {
              resizeObserver.observe(stableAncestor);
            } else {
              console.warn('Pagination: expected .tiptap-canvas-wrap ancestor not found; resize-based recompute will be skipped.');
            }

            const imageLoadHandler = (e: Event) => {
              if ((e.target as HTMLElement)?.tagName === 'IMG') scheduleRecompute();
            };
            view.dom.addEventListener('load', imageLoadHandler, true);

            return {
              update(updatedView, prevState) {
                if (!updatedView.state.doc.eq(prevState.doc)) {
                  scheduleRecompute();
                }
              },
              destroy() {
                if (scheduled) clearTimeout(scheduled);
                resizeObserver.disconnect();
                view.dom.removeEventListener('load', imageLoadHandler, true);
              },
            };
          },
        }),
      ];
    },
  });
}

// ─── Main Component ───────────────────────────────────────────────────────────

@Component({
    selector: 'app-doceditor',
    templateUrl: './doceditor.component.html',
    styleUrls: ['./doceditor.component.scss'],
    standalone: false
})
export class DoceditorComponent implements OnInit, AfterViewInit, OnDestroy {

  // ─── View Mode ────────────────────────────────────────────────────────────
  viewMode: 'list' | 'editor' = 'list';

  // ─── List View Props ──────────────────────────────────────────────────────
  documents: any[] = [];
  searchText: string = '';
  createdBy: string = '';
  errorMsg: boolean = false;
  editDoc: any = null;
  isReverse: boolean = false;
  sortKey: string = '';

  // ─── Pagination ───────────────────────────────────────────────────────────
  currentPage: number = 1;
  pageSize: number = 10;

  showSubscriptionPopup = false;
  getPlan:any;
  getCategory:any;
  restrictedPlans = ['free', 'professional', 'starter'];

  /** Documents after applying the search filter. */
  get filteredDocuments(): any[] {
    if (!this.searchText?.trim()) return this.documents;
    const q = this.searchText.trim().toLowerCase();
    return this.documents.filter((d: any) =>
      (d.documentname || '').toLowerCase().includes(q)
    );
  }

  /** The slice of filteredDocuments shown on the current page. */
  get pagedDocuments(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredDocuments.slice(start, start + this.pageSize);
  }

  get canGoToPrevious(): boolean {
    return this.currentPage > 1;
  }

  get canGoToNext(): boolean {
    return this.currentPage * this.pageSize < this.filteredDocuments.length;
  }

  prevPage(): void {
    if (this.canGoToPrevious) this.currentPage--;
  }

  nextPage(): void {
    if (this.canGoToNext) this.currentPage++;
  }

  // ─── Modal State Flags ────────────────────────────────────────────────────
  showViewModal: boolean = false;

  // ─── Editor View Props ────────────────────────────────────────────────────
  @ViewChild('editorEl', { static: false }) editorEl!: ElementRef;
  @ViewChild('canvasWrap', { static: false }) canvasWrapEl!: ElementRef<HTMLDivElement>;
  @ViewChild('imageFileInput') imageFileInput!: ElementRef;
  @ViewChild('menuTrigger1') menuTrigger1!: MatMenuTrigger;
  @ViewChild('menuTrigger3') menuTrigger3!: MatMenuTrigger;
  @ViewChild('docNameInput') docNameInput!: ElementRef<HTMLInputElement>;
  @ViewChild('previewFrameWrap', { static: false }) previewFrameWrapEl!: ElementRef<HTMLDivElement>;
  @ViewChild('previewCanvasScroll', { static: false }) previewCanvasScrollEl!: ElementRef<HTMLDivElement>;
  @ViewChild('previewPage', { static: false }) previewPageEl!: ElementRef<HTMLDivElement>;
  @ViewChild('pdfCanvas', { static: false }) pdfCanvasEl!: ElementRef<HTMLCanvasElement>;
  @ViewChild('annotationLayer', { static: false }) annotationLayerEl!: ElementRef<HTMLDivElement>;
  @ViewChild('tablePickerEl') tablePickerEl?: ElementRef<HTMLElement>;
  @ViewChild('linkPopoverEl') linkPopoverEl?: ElementRef<HTMLElement>;
  @ViewChild('linkUrlInput') linkUrlInputEl?: ElementRef<HTMLInputElement>;
  // {read: ElementRef} because #insertMenuBtn has `mat-button` applied — MatButton
  // is itself a Component, so a bare template ref on that element would resolve
  // to the MatButton instance, not the native <button>.
  @ViewChild('insertMenuBtn', { read: ElementRef }) insertMenuBtnEl?: ElementRef<HTMLElement>;

  editor!: Editor;
  title = '';
  author = '';
  documentname = '';
  documentId: any;
  pageId: any;
  docSaved = false;

  editingDocName = false;

  latexdoc = environment.lateXAPI;
  latexImageFolder = environment.latexImageFolder;
  product = environment.product;

  // ─── Table grid picker (Google-Docs style Insert > Table) ─────────────────
  // The page is a fixed ~672px-wide printable area (US-Letter minus margins),
  // not an infinitely-resizable spreadsheet canvas — Google Docs' own picker
  // cap (20x20) assumes adjustable column widths and page size, which this
  // legal-doc page doesn't have, so both the picker and the running +Row/+Col
  // ceiling below are capped lower to stay legible/printable at 12 columns.
  readonly TABLE_PICKER_MIN_COLS = 6;
  readonly TABLE_PICKER_MIN_ROWS = 6;
  readonly TABLE_PICKER_MAX = 12;
  // Separate, permanent ceiling on a table's overall size — applies to the
  // picker AND to every +Row/+Col addition made later, so a table can never
  // grow into something wider than the printable page or absurdly tall.
  readonly MAX_TABLE_COLS = 12;
  readonly MAX_TABLE_ROWS = 100;
  showTablePicker = false;
  tablePickerPos: { top: number; left: number } = { top: 0, left: 0 };
  tablePickerGridRows = this.TABLE_PICKER_MIN_ROWS;
  tablePickerGridCols = this.TABLE_PICKER_MIN_COLS;
  tablePickerHoverRow = 0;
  tablePickerHoverCol = 0;

  get tablePickerRowIndices(): number[] {
    return Array.from({ length: this.tablePickerGridRows }, (_, i) => i + 1);
  }
  get tablePickerColIndices(): number[] {
    return Array.from({ length: this.tablePickerGridCols }, (_, i) => i + 1);
  }
  get tablePickerLabel(): string {
    return this.tablePickerHoverRow && this.tablePickerHoverCol
      ? `${this.tablePickerHoverCol} x ${this.tablePickerHoverRow} Table`
      : 'Insert table';
  }

  // ─── Link insertion/editing popover ────────────────────────────────────────
  showLinkPopover = false;
  linkPopoverPos: { top: number; left: number } = { top: 0, left: 0 };
  linkPopoverUrl = '';
  linkPopoverMode: 'create' | 'edit' = 'create';
  private linkPopoverRange: { from: number; to: number } | null = null;

  showPreviewDoc = false;
  private previewObjectUrl: string | null = null;

  // ─── PDF rendering state (PDF.js) ────────────────────────────────────────
  private pdfDoc: any = null;
  private pdfRenderTask: any = null;

  pdfCurrentPage = 1;
  pdfTotalPages = 1;
  pdfZoom = 100;

  pdfPageEditing = false;
  @ViewChild('pdfPageInput') pdfPageInputEl!: ElementRef<HTMLInputElement>;

  showThumbnailPanel = false;
  pdfThumbnails: { pageNumber: number; dataUrl: string }[] = [];
  private generatingThumbnails = false;

  get pendingThumbnailSlots(): number[] {
    const remaining = this.pdfTotalPages - this.pdfThumbnails.length;
    return remaining > 0 ? new Array(remaining).fill(0) : [];
  }

  private pdfPageWidthPt = 595.28;
  private pdfPageHeightPt = 841.89;

  // ─── Autosave ─────────────────────────────────────────────────────────────
  private autosaveTimer: any = null;
  isSaving = false;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  private lastSavedHtml = '';
  private lastSavedTitle = '';
  private lastSavedDocumentName = '';

  get saveStatusLabel(): string {
    switch (this.saveStatus) {
      case 'saving': return 'Saving…';
      case 'saved': return 'All changes saved';
      case 'error': return 'Save failed';
      default: return '';
    }
  }

  // ─── Editor canvas scroll ─────────────────────────────────────────────────
  private onEditorCanvasScroll = () => {
    this.closeLinkPopover();
  };

  constructor(
    private router: Router,
    private http: HttpClient,
    private httpservice: HttpService,
    private toast: ToastrService,
    private documentService: DocumentService,
    private modalService: ModalService,
    private spinnerService: NgxSpinnerService,
    private confirmationDialogService: ConfirmationDialogService,
    public sanitizer: DomSanitizer,
    public dialog: MatDialog,
    private idGenerator: RandomService,
    private sidebarService: SidebarService,
  ) {}

  ngOnInit() {
    this.author = localStorage.getItem('name') || '';
    this.createdBy = localStorage.getItem('name') || '';
    this.getPlan = localStorage.getItem("plan"); // get current plan
    this.loadDocuments();
  }

  ngAfterViewInit() {
    // Editor initialised lazily when switching to editor view
  }

  ngOnDestroy() {
    // Leaving the page while still in the editor: hand the sidebar back to the
    // user's saved preference so the collapse doesn't leak into other routes.
    if (this.viewMode === 'editor') {
      this.sidebarService.restore();
    }
    this.destroyEditor();
    this.teardownPdfPreview();
  }

  // ─── LIST VIEW ────────────────────────────────────────────────────────────

  loadDocuments() {
    this.spinnerService.show();
    this.httpservice.sendGetLatexDoc(URLUtils.getDocument).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        this.documents = res || [];
        this.errorMsg = this.documents.length === 0;
        this.currentPage = 1;
      },
      (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load documents.');
        this.documents = [];
      }
    );
  }

    //subscription check for action button
  checkPlan(event?: MouseEvent): boolean {
    const restrictedPlans = ['free', 'professional', 'starter'];

    if (restrictedPlans.includes(this.getPlan)) {

      this.dialog.open(ConfirmPlanComponent, {
        width: '400px',
        data: { plan: this.getPlan }
      });

      return false;   // Stop further execution
    }
    return true;      // Allow further execution
  }

  newDocument() {
    if (!ensureActiveSubscription(this.confirmationDialogService)) return;

    // Stop here if plan is restricted
    if (!this.checkPlan()) {
      return;
    }

    this.destroyEditor();

    this.documentId = null;
    this.pageId = null;
    this.title = '';
    this.docSaved = false;
    this.editingDocName = false;
    this.saveStatus = 'idle';

    this.documentname = this.generateDefaultDocName();
    this.lastSavedDocumentName = '';

    this.teardownPdfPreview();

    this.viewMode = 'editor';
    this.sidebarService.collapse();
    setTimeout(() => this.initEditor(), 100);
  }

  editDocument(doc: any) {
    if (!ensureActiveSubscription(this.confirmationDialogService)) return;

    this.destroyEditor();

    this.documentId = doc.docid;
    this.documentname = doc.documentname;
    this.lastSavedDocumentName = (doc.documentname || '').trim();
    this.pageId = null;
    this.title = '';
    this.docSaved = false;
    this.editingDocName = false;
    this.saveStatus = 'idle';

    this.teardownPdfPreview();

    this.viewMode = 'editor';
    this.sidebarService.collapse();
    setTimeout(() => {
      this.initEditor();
      this.openFile(doc.docid);
    }, 100);
  }

  // ─── View Document Modal ──────────────────────────────────────────────────
  pdfSrc!: SafeResourceUrl;

  viewDocumentModal(doc: any) {
    if (!this.documents || !Array.isArray(this.documents)) return;

    const found = this.documents.find((d: any) => d.docid === doc.docid);
    if (!found) {
      this.toast.error('Document not found!');
      return;
    }

    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }

    this.pdfSrc = undefined!;
    this.showViewModal = true;
    this.spinnerService.show();

    const url = `${this.latexdoc}/v1/document/openview/${found.docid}`;
    this.http.get(url, { responseType: 'arraybuffer' }).subscribe(
      (pdfBytes: ArrayBuffer) => {
        this.spinnerService.hide();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        this.previewObjectUrl = URL.createObjectURL(blob);
        this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.previewObjectUrl + '#toolbar=0&navpanes=0&scrollbar=0&view=FitH'
        );
      },
      () => {
        this.spinnerService.hide();
        this.toast.error('Failed to load document preview.');
        this.showViewModal = false;
      }
    );
  }

  closeViewModal() {
    this.showViewModal = false;
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
    this.pdfSrc = undefined!;
  }

  onModalBackdropClick(event: MouseEvent) {
    this.closeViewModal();
  }

  // ─── Delete Flow ──────────────────────────────────────────────────────────

  deleteDocumentClick(doc: any) {
    this.editDoc = JSON.parse(JSON.stringify(doc));
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Confirmation', message: `Are you sure do you want to delete this Document?` }
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.confirmDelete();
      } else {
        this.editDoc = null;
      }
    });
  }

  confirmDelete() {
    if (!this.editDoc?.docid) return;

    this.spinnerService.show();
    this.httpservice.sendDeleteLatexRequest(URLUtils.deleteDocid(this.editDoc.docid)).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        if (!res.error) {
          this.editDoc = null;
          this.loadDocuments();
          this.toast.success(res.msg || 'Document deleted successfully.');
        } else {
          this.toast.error(res.msg);
        }
      },
      (err: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.toast.error(err.error?.msg || 'Delete failed.');
      }
    );
  }

  closeModal(id: string) {
    this.modalService.close(id);
  }

  // ─── List Actions ─────────────────────────────────────────────────────────

  viewDocument(doc: any) {
    this.viewDocumentModal(doc);
  }

  deleteDocument(doc?: any) {
    const target = doc || this.editDoc;
    if (!target?.docid) return;

    this.spinnerService.show();
    this.httpservice.sendDeleteLatexRequest(URLUtils.deleteDocid(target.docid)).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        if (!res.error) {
          this.loadDocuments();
          this.toast.success(res.msg || 'Document deleted successfully.');
        } else {
          this.toast.error(res.msg);
        }
      },
      (err: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.toast.error(err.error?.msg || 'Delete failed.');
      }
    );
  }

  sortingFile(val: any) {
    this.isReverse = !this.isReverse;
    if (this.isReverse) {
      this.documents = this.documents?.sort((p1: any, p2: any) => (p1[val] < p2[val]) ? 1 : (p1[val] > p2[val]) ? -1 : 0);
    } else {
      this.documents = this.documents?.sort((p1: any, p2: any) => (p1[val] > p2[val]) ? 1 : (p1[val] < p2[val]) ? -1 : 0);
    }
    this.currentPage = 1;
  }

  sortingDateFile(val: string) {
    if (this.sortKey === val) {
      this.isReverse = !this.isReverse;
    } else {
      this.sortKey = val;
      this.isReverse = false;
    }
    const sortedDocuments = JSON.parse(JSON.stringify(this.documents));
    sortedDocuments.sort((a: any, b: any) => {
      const dateA = a[val]?.["$date"] ? new Date(a[val].$date) : null;
      const dateB = b[val]?.["$date"] ? new Date(b[val].$date) : null;
      if (dateA && dateB) { return dateA.getTime() - dateB.getTime(); }
      else if (!dateA && !dateB) { return 0; }
      else if (!dateA) { return 1; }
      else { return -1; }
    });
    if (this.isReverse) { sortedDocuments.reverse(); }
    this.documents = sortedDocuments;
    this.currentPage = 1;
  }

  restricttextSpace(event: any) {
    let inputValue: string = event.target.value;
    inputValue = inputValue.replace(/^\s+/, '');
    inputValue = inputValue.replace(/\s{2,}/g, ' ');
    event.target.value = inputValue;
  }

  /** Resets to page 1 whenever the search text changes. */
  onSearchInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    let v = el.value.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
    el.value = v;
    this.searchText = v;
    this.currentPage = 1;
  }

  backToList() {
    this.viewMode = 'list';
    this.sidebarService.restore();
    this.destroyEditor();
    this.loadDocuments();
  }

  // ─── EDITOR LIFECYCLE ────────────────────────────────────────────────────

  private destroyEditor() {
    this.stopAutosave();
    this.canvasWrapEl?.nativeElement.removeEventListener('scroll', this.onEditorCanvasScroll);
    this.showTablePicker = false;
    this.showLinkPopover = false;
    if (this.editor) {
      this.editor.destroy();
      this.editor = undefined!;
    }
  }

  initEditor() {
    if (this.editor) return;

    // Publish the shared page geometry/typography as CSS custom properties so
    // the stylesheet and the pagination extension read the same numbers.
    if (this.canvasWrapEl?.nativeElement) {
      applyDocLayoutCssVars(this.canvasWrapEl.nativeElement);
    }

    this.editor = new Editor({
      element: this.editorEl.nativeElement,
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          bulletList: {},
          orderedList: {},
          link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } },
        }),
        Underline,
        TextStyle,
        Color,
        FontSize,
        // docTitle intentionally omitted — the Title block manages its own
        // `align` attribute (see TitleNode) so left alignment renders correctly.
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        // Inline styles baked into every rendered <table>/<th>/<td> so the exact
        // same markup (getHTML() output, persisted as-is and sent to the LaTeX/
        // PDF service) renders identically wherever it's consumed — the editor's
        // look previously lived only in this component's scoped stylesheet,
        // which never travels with the saved HTML.
        Table.configure({
          resizable: false,
          HTMLAttributes: { style: 'width:100%;border-collapse:collapse;table-layout:fixed;font-size:12pt;margin:0.8em 0;' },
        }),
        TableRow,
        TableHeader.configure({
          HTMLAttributes: { style: 'font-weight:700;padding:2px 6px;border:1px solid #000;text-align:left;' },
        }),
        TableCell.configure({
          HTMLAttributes: { style: 'padding:2px 6px;border:1px solid #000;vertical-align:top;' },
        }),
        Image.configure({ inline: false, allowBase64: false }),
        OverviewNode,
        PageBreakNode,
        TitleNode,
        AutoCapitalizeHeadings,
        HeadingNumbering,
        JoinAdjacentLists,
        IndentExtension,
        createPaginationExtension(),
      ],
      // Google-Docs-like: new documents open with an empty body paragraph so the
      // default block is normal text; headings are applied on demand from the
      // toolbar. Existing docs override this via setContent() in openFile().
      content: '<p></p>',
      editorProps: {
        attributes: { class: 'tiptap-content', spellcheck: 'true' },
        // Google-Docs-style: clicking an existing link opens the edit popover
        // instead of navigating (the link mark is already configured
        // openOnClick:false above, so this doesn't fight a navigation attempt).
        // Ctrl/Cmd+click follows the link instead, matching Docs/Notion/etc.
        handleClick: (view, _pos, event) => {
          const anchorEl = (event.target as HTMLElement)?.closest?.('a');
          if (!anchorEl || !view.dom.contains(anchorEl)) return false;

          if (event.ctrlKey || event.metaKey) {
            const href = anchorEl.getAttribute('href');
            if (href) window.open(href, '_blank', 'noopener,noreferrer');
            return true;
          }

          setTimeout(() => {
            // posAtDOM converts a DOM node -> document position (the inverse of
            // coordsAtPos); +1 lands inside the mark rather than at its
            // boundary, where mark membership is ambiguous.
            const linkPos = view.posAtDOM(anchorEl, 0) + 1;
            this.editor.chain().setTextSelection(linkPos).run();
            this.openLinkPopoverForSelection('edit');
          });
          return false; // don't swallow the click — let normal caret placement proceed
        },
        // Google-Docs-style Ctrl/Cmd+K: open the link popover for the current
        // selection (or the link the caret is inside of) without a mouse.
        handleKeyDown: (_view, event) => {
          if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            this.insertLink();
            return true;
          }
          return false;
        },
      },
      onUpdate: () => {
        this.syncTitleFromDoc();
      },
      // Google-Docs-style persistent caret support: continuously track the
      // last known selection independent of DOM focus, so toolbar actions
      // (Page Break, etc.) and focus restoration always target the right spot
      // even after the editor has lost DOM focus.
      onSelectionUpdate: () => {
        const { from, to } = this.editor.state.selection;
        this.lastKnownSelection = { from, to };
      },
      // Whenever the editor loses DOM focus for a reason that isn't the user
      // deliberately choosing another editable surface (an input, an open
      // menu/popover, etc.), bring focus — and the caret — right back, exactly
      // where it was. This is what keeps the caret blinking like Google Docs
      // even after clicking toolbar chrome or blank space outside the page.
      onBlur: () => {
        this.scheduleFocusRestore();
      },
    });

    this.lastSavedHtml = this.editor.getHTML();
    this.lastSavedTitle = this.title;
    this.startAutosave();

    setTimeout(() => {
      this.canvasWrapEl?.nativeElement.addEventListener('scroll', this.onEditorCanvasScroll, { passive: true } as any);
      // Google-Docs-style: the editor should always land with a visible,
      // blinking caret as soon as it's opened, instead of waiting for the
      // user's first click. For existing documents, openFile() re-focuses
      // again once the loaded content is in place (see below).
      this.editor?.commands.focus('end', { scrollIntoView: false });
    }, 0);
  }

  // ─── Toolbar ─────────────────────────────────────────────────────────────

  setHeading(level: 1 | 2 | 3) {
    this.editor.chain().focus().toggleHeading({ level }).run();
  }

  /**
   * Google-Docs "Title" style — a dedicated docTitle block at the top of the
   * body. Defaults to centered (its `align` attribute), but the user can then
   * re-align it left / right / justify via the toolbar just like any other
   * block; the compiled PDF honours the chosen alignment (see html_to_latex).
   */
  setTitle() {
    this.editor.chain().focus().setNode('docTitle').run();
  }

  getHeadingLabel(): string {
    if (!this.editor) return 'Normal text';
    if (this.editor.isActive('docTitle')) return 'Title';
    if (this.editor.isActive('heading', { level: 1 })) return 'Section';
    if (this.editor.isActive('heading', { level: 2 })) return 'Sub Section';
    return 'Normal text';
  }

  // ─── Insert menu: Link ────────────────────────────────────────────────────
  insertLink() {
    if (!this.editor) return;
    const { empty } = this.editor.state.selection;
    const isOnLink = this.editor.isActive('link');
    if (empty && !isOnLink) {
      this.toast.error('Select some text first to add a link.');
      return;
    }
    this.openLinkPopoverForSelection(isOnLink ? 'edit' : 'create');
  }

  /** Keep the PDF `title` field in sync with the in-body Title block, if present. */
  private syncTitleFromDoc() {
    if (!this.editor) return;
    let found: string | null = null;
    this.editor.state.doc.descendants((node: any) => {
      if (found === null && node.type.name === 'docTitle') {
        found = (node.textContent || '').trim();
      }
    });
    // Mirror the in-body Title block into the PDF `title` field — INCLUDING the
    // empty case. When the user deletes the Title block (or all content), the
    // title must clear too; otherwise the stale title kept getting re-emitted as
    // a top-of-page \maketitle, so the preview still showed the old title after
    // the document was emptied.
    this.title = found || '';
  }

  // The Title block <h1 data-type="doc-title"> now stays INLINE in the saved
  // body so it renders in the PDF exactly where the user placed it (the backend
  // converts it to centered large text in html_to_latex, never a \section). This
  // regex still exists to detect a title block in legacy/backend HTML.
  private static readonly DOC_TITLE_RE =
    /<h1[^>]*\bdata-type=["']doc-title["'][^>]*>[\s\S]*?<\/h1>/i;

  // Rebuild the editor body from what the backend returns.
  //   • New documents already carry the Title block inline in the body — use it
  //     as-is so its position (and everything around it) is preserved.
  //   • Legacy documents stored the title only in the separate `title` field
  //     (stripped from the body); for those we re-insert a Title block at the top
  //     so the title still shows. Kept as an HTML transform (not editor commands)
  //     so it can run in a single setContent() call during openFile().
  private buildEditorHtml(bodyHtml: string, title: string): string {
    const body = (bodyHtml || '').trim();
    // New format: title already lives inline — do not move or duplicate it.
    if (DoceditorComponent.DOC_TITLE_RE.test(body)) {
      return body || '<p></p>';
    }
    // Legacy format: reconstruct the title block at the top from the title field.
    const t = (title || '').trim();
    if (t) {
      const esc = t
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return `<h1 data-type="doc-title" style="text-align: center">${esc}</h1>` + body;
    }
    return body || '<p></p>';
  }

  // ─── New Google-Docs-style formatting ────────────────────────────────────

  setAlign(align: 'left' | 'center' | 'right' | 'justify') {
    if (!this.editor) return;
    // The Title block isn't a TextAlign node — it carries its own `align`
    // attribute — so setTextAlign() would silently no-op on it. Route the
    // title through updateAttributes instead so its alignment actually changes.
    if (this.editor.isActive('docTitle')) {
      this.editor.chain().focus().updateAttributes('docTitle', { align }).run();
      return;
    }
    this.editor.chain().focus().setTextAlign(align).run();
  }

  /** Toolbar active-state for the alignment buttons, Title-aware. */
  isAlignActive(align: 'left' | 'center' | 'right' | 'justify'): boolean {
    if (!this.editor) return false;
    if (this.editor.isActive('docTitle')) {
      return (this.editor.getAttributes('docTitle')?.['align'] || 'center') === align;
    }
    return this.editor.isActive({ textAlign: align });
  }

  fontSizes: string[] = ['10px', '12px', '14px', '16px', '18px', '24px', '30px', '36px'];

  setFontSize(size: string) {
    if (!size) {
      this.editor.chain().focus().unsetFontSize().run();
    } else {
      this.editor.chain().focus().setFontSize(size).run();
    }
  }

  getFontSize(): string {
    if (!this.editor) return '';
    const size = this.editor.getAttributes('textStyle')?.['fontSize'];
    // The editor body has no explicit fontSize by default — it inherits the
    // 12pt base from .tiptap-editor, which is 16px (12 × 4/3). Show "16" so the
    // dropdown reflects the size actually rendered instead of a misleading "12".
    return size ? String(size).replace('px', '') : '16';
  }

  setColor(event: Event) {
    const color = (event.target as HTMLInputElement)?.value;
    if (color) this.editor.chain().focus().setColor(color).run();
  }

  // Google-Docs text-colour palette: 10 columns × 8 rows. Row 1 greyscale, row 2
  // the primary hues, rows 3–8 six tint/shade steps of each hue. Replaces the
  // native <input type="color">, whose OS dialog opened an unusable hue-wheel on
  // this platform (the reported "outer circle" bug).
  textColorPalette: string[][] = [
    ['#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff'],
    ['#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff'],
    ['#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc'],
    ['#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd'],
    ['#cc4125', '#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6d9eeb', '#6fa8dc', '#8e7cc3', '#c27ba0'],
    ['#a61c00', '#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3c78d8', '#3d85c6', '#674ea7', '#a64d79'],
    ['#85200c', '#990000', '#b45f06', '#bf9000', '#38761d', '#134f5c', '#1155cc', '#0b5394', '#351c75', '#741b47'],
    ['#5b0f00', '#660000', '#783f04', '#7f6000', '#274e13', '#0c343d', '#1c4587', '#073763', '#20124d', '#4c1130'],
  ];

  // Most-recently used custom colours (Google-Docs "CUSTOM" row).
  recentColors: string[] = [];

  applyColor(color: string) {
    if (color) this.editor?.chain().focus().setColor(color).run();
  }

  /** Apply a custom colour picked from the native input and remember it. */
  applyCustomColor(event: Event) {
    const color = (event.target as HTMLInputElement)?.value;
    if (!color) return;
    this.applyColor(color);
    this.recentColors = [color, ...this.recentColors.filter((c) => c !== color)].slice(0, 8);
  }

  clearColor() {
    this.editor?.chain().focus().unsetColor().run();
  }

  /**
   * Colour currently applied at the caret / selection, so the toolbar's colour
   * indicator (the bar under the "A") reflects the selected text's colour and
   * the native picker opens on it. Falls back to black when no colour is set.
   */
  getActiveColor(): string {
    if (!this.editor) return '#000000';
    return this.editor.getAttributes('textStyle')?.['color'] || '#000000';
  }

  setParagraph() {
    this.editor.chain().focus().setParagraph().run();
  }

  /**
   * Bulleted/Numbered toolbar buttons. Converting one list type to the other
   * used to call TipTap's toggleBulletList/toggleOrderedList, which only
   * re-wraps the list level at the cursor — nested sublists of the other type
   * were left behind, so a numbered list with an indented sublist turned into a
   * broken mix (and lost indentation). Instead we retype EVERY list node inside
   * the outermost list in one shot with setNodeMarkup, which keeps all list
   * items, their content, and the full nesting/indentation intact.
   */
  setListType(target: 'bulletList' | 'orderedList') {
    if (!this.editor) return;
    const view = this.editor.view;
    const state = view.state;
    const targetType = state.schema.nodes[target];
    if (!targetType) return;

    const { $from } = state.selection;

    // Outermost list ancestor of the caret (the whole list, not just its level).
    let listDepth = -1;
    for (let d = $from.depth; d >= 1; d--) {
      const name = $from.node(d).type.name;
      if (name === 'bulletList' || name === 'orderedList') listDepth = d;
    }

    // Not in a list, or already this exact type at the top → fall back to the
    // native toggle (create a new list, or toggle the list off).
    const toggle = target === 'bulletList' ? 'toggleBulletList' : 'toggleOrderedList';
    if (listDepth === -1 || $from.node(listDepth).type === targetType) {
      (this.editor.chain().focus() as any)[toggle]().run();
      return;
    }

    // Retype the outer list and every nested list within it to the target type.
    // setNodeMarkup keeps node sizes, so the collected positions stay valid as
    // we apply each change in the same transaction.
    const listPos = $from.before(listDepth);
    const outer = $from.node(listDepth);
    const positions: number[] = [];
    state.doc.nodesBetween(listPos, listPos + outer.nodeSize, (node: any, pos: number) => {
      const name = node.type.name;
      if ((name === 'bulletList' || name === 'orderedList') && node.type !== targetType) {
        positions.push(pos);
      }
    });

    const tr = state.tr;
    positions.forEach((pos) => tr.setNodeMarkup(pos, targetType));
    view.dispatch(tr);
    view.focus();
  }

  // DOM lookup (not a ProseMirror doc walk) so the same row()/cells.length
  // counting logic as the hover controls (tableRowCount/tableColCount) applies.
  private getActiveTableEl(): HTMLTableElement | null {
    if (!this.editor) return null;
    const domInfo = this.editor.view.domAtPos(this.editor.state.selection.from);
    const node = domInfo.node.nodeType === Node.TEXT_NODE ? domInfo.node.parentElement : domInfo.node as HTMLElement;
    return (node as HTMLElement)?.closest?.('table') ?? null;
  }

  addTableRow() {
    const table = this.getActiveTableEl();
    if (table && this.tableRowCount(table) >= this.MAX_TABLE_ROWS) {
      this.toast.error(`Tables are limited to ${this.MAX_TABLE_ROWS} rows.`);
      return;
    }
    this.editor.chain().focus().addRowAfter().run();
  }

  addTableCol() {
    const table = this.getActiveTableEl();
    if (table && this.tableColCount(table) >= this.MAX_TABLE_COLS) {
      this.toast.error(`Tables are limited to ${this.MAX_TABLE_COLS} columns.`);
      return;
    }
    this.editor.chain().focus().addColumnAfter().run();
  }

  deleteTable() { this.editor.chain().focus().deleteTable().run(); }

  insertOverview() {
    if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    const doc = this.editor.getJSON();
    const hasOverview = doc.content?.some((n: any) => n.type === 'overview');
    if (hasOverview) {
      this.toast.error('Only one overview is allowed.');
      return;
    }
    this.editor.chain().focus()
      .insertContentAt(0, { type: 'overview', content: [{ type: 'text', text: 'Overview text...' }] })
      .run();
  }

private lastKnownSelection: { from: number; to: number } | null = null;

// Call this from (mousedown) on the toolbar/insert menu trigger, or right
// before opening any mat-menu that can steal focus from the editor. In
// practice `lastKnownSelection` is kept fresh automatically by the editor's
// onSelectionUpdate callback above; this remains as an explicit, belt-and-
// suspenders capture point at the exact moment a mousedown could steal focus.
 captureSelection() {
  if (!this.editor) return;
  const { from, to } = this.editor.state.selection;
  this.lastKnownSelection = { from, to };
}

// Google-Docs-style persistent caret: if the editor's DOM focus is lost for
// any reason other than the user deliberately choosing another legitimate
// editable surface (a text input, an open toolbar menu/popover, etc.), put
// focus — and the caret — right back where it was. This is the fallback for
// cases preserveEditorFocus()'s mousedown-preventDefault can't catch, such as
// a Material CDK overlay/menu grabbing focus programmatically rather than via
// a plain mousedown.
private scheduleFocusRestore() {
  setTimeout(() => {
    if (!this.editor || this.editor.isDestroyed) return;
    if (this.editor.view.hasFocus()) return;

    const active = document.activeElement as HTMLElement | null;
    if (active && active !== document.body) {
      const isLegitimateFocusTarget = !!active.closest(
        'input, textarea, select, [contenteditable="true"], ' +
        '.mat-mdc-menu-panel, .mat-menu-panel, .cdk-overlay-container, ' +
        '.tb-link-popover, .tb-grid-picker'
      );
      if (isLegitimateFocusTarget) return;
    }

    const sel = this.lastKnownSelection;
    if (sel) {
      this.editor.chain().setTextSelection(sel).focus(undefined, { scrollIntoView: false }).run();
    } else {
      this.editor.commands.focus(undefined, { scrollIntoView: false });
    }
  }, 0);
}

insertPageBreak() {
  if (!ensureActiveSubscription(this.confirmationDialogService)) return;
  if (!this.editor) return;

  if (this.lastKnownSelection) {
    this.editor.commands.setTextSelection(this.lastKnownSelection);
  }

  const doc = this.editor.getJSON();
  const nodes = doc.content ?? [];
  if (nodes.length === 0) {
    this.toast.error('Page Break cannot be added at the beginning of the document.');
    return;
  }

  let blockedByConsecutiveBreak = false;

  const inserted = this.editor
    .chain()
    .focus()
    .command(({ tr, dispatch, state }) => {
      const pageBreakType = state.schema.nodes['pageBreak'];
      if (!pageBreakType) return false;

      // If the user had an active text selection (not just a collapsed
      // caret), the page break replaces the selected content instead of
      // being inserted beside it — exactly like Google Docs.
      const { from: selFrom, to: selTo } = tr.selection;
      if (selFrom !== selTo) {
        tr.deleteSelection();
      }

      const pos = tr.selection.$from;
      const topLevelDepth = 1;
      if (pos.depth < topLevelDepth) return false;

      const atBlockStart = pos.parentOffset === 0;
      const atBlockEnd = pos.parentOffset === pos.parent.content.size;
      const isTopLevel = pos.depth === topLevelDepth;

      let insertPos: number;

      if (isTopLevel && atBlockStart) {
        insertPos = pos.before(topLevelDepth);
      } else if (isTopLevel && atBlockEnd) {
        insertPos = pos.after(topLevelDepth);
      } else if (pos.parent.isTextblock) {
        tr.split(pos.pos);
        const mapped = tr.doc.resolve(tr.mapping.map(pos.pos));
        insertPos = mapped.depth > topLevelDepth
          ? mapped.before(topLevelDepth + 1)
          : mapped.pos;
      } else {
        insertPos = pos.pos;
      }

      // Reliable consecutive-break guard: inspect the actual top-level nodes
      // on either side of the computed insertion boundary, instead of
      // $from.nodeBefore. The caret-depth check misses the common case where
      // the caret has drifted one level deeper — inside the paragraph that
      // immediately follows an existing break — which is exactly where it
      // lands right after a break is inserted. That gap let a second,
      // often-accidental click (no visual feedback appears for ~120ms)
      // stack a duplicate break with nothing real between them.
      const boundary = tr.doc.resolve(insertPos);
      if (boundary.nodeBefore?.type.name === 'pageBreak' ||
          boundary.nodeAfter?.type.name === 'pageBreak') {
        blockedByConsecutiveBreak = true;
        return false;
      }

      const pageBreakNode = pageBreakType.create();

      // A page break as the very first node leaves no editable area above it
      // (you can't click or type into page 1) and LaTeX collapses a leading
      // \newpage, so the preview stays a single page. Give it a real first page
      // by prepending an empty paragraph and placing the break right after it.
      if (insertPos === 0) {
        const paragraphType = state.schema.nodes['paragraph'];
        if (paragraphType) {
          const emptyPara = paragraphType.create();
          tr.insert(0, emptyPara);
          insertPos = emptyPara.nodeSize;
        }
      }

      tr.insert(insertPos, pageBreakNode);

      const afterPos = Math.min(insertPos + pageBreakNode.nodeSize, tr.doc.content.size);
      tr.setSelection(TextSelection.near(tr.doc.resolve(afterPos), 1));

      if (dispatch) dispatch(tr);
      return true;
    })
    .run();

  if (!inserted && blockedByConsecutiveBreak) {
    this.toast.error('Consecutive page breaks are not allowed.');
  }
}

  insertImage() {
    if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    this.imageFileInput.nativeElement.click();
  }

  handleImageUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];

    if (file.size > 2 * 1024 * 1024) { this.toast.error('File size exceeds 2 MB.'); return; }
    if (!['image/jpeg', 'image/png'].includes(file.type)) { this.toast.error('Please upload jpg or png image.'); return; }

    let name = file.name.replace(/\s/g, '');
    const parts = name.split('.');
    if (parts.length > 1) name = parts.slice(0, -1).join('') + '.' + parts.slice(-1);
    const newFile = new File([file], name, { type: file.type });

    const formData = new FormData();
    formData.append('file', newFile);

    this.spinnerService.show();
    this.httpservice.sendPostLatexRequest(URLUtils.addImage, formData).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        this.toast.success('Image uploaded successfully');
        const userId = localStorage.getItem('user_id');
        const src = `${this.latexImageFolder}/${userId}/${res.fileName}`;
        this.editor.chain().focus()
          .insertContent(`<img src="${src}" data-filename="${res.fileName}" alt="uploaded image" />`)
          .run();
        input.value = '';
      },
      (err: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.toast.error(err.error?.message || 'Image upload failed');
      }
    );
  }

  restrictMetaInput(event: Event) {
    const el = event.target as HTMLInputElement;
    el.value = el.value.replace(/[^A-Za-z0-9\-,_!@$%^*;'&/.()\|# ]/g, '').replace(/^ /, '');
  }

  // ─── Inline document-name editing ────────────────────────────────────────

  startEditDocName() {
    this.editingDocName = true;
    setTimeout(() => this.docNameInput?.nativeElement?.focus(), 0);
  }

  finishEditDocName() {
    this.editingDocName = false;
    const trimmed = (this.documentname || '').trim();
    this.documentname = trimmed || this.generateDefaultDocName();
  }

  private generateDefaultDocName(): string {
    const base = 'Untitled';
    const existing = new Set((this.documents || []).map((d: any) => d.documentname));
    if (!existing.has(base)) return base;
    let i = 1;
    while (existing.has(`${base} ${i}`)) i++;
    return `${base} ${i}`;
  }

  // ─── Save Flow ────────────────────────────────────────────────────────────

  saveDocument() {
    this.performSave({ silent: false });
  }

  private startAutosave() {
    this.stopAutosave();
    this.autosaveTimer = setInterval(() => this.autosaveTick(), 4000);
  }

  private stopAutosave() {
    if (this.autosaveTimer) {
      clearInterval(this.autosaveTimer);
      this.autosaveTimer = null;
    }
  }

  private autosaveTick() {
    if (!this.editor || this.isSaving) return;
    const html = this.editor.getHTML();
    // Allow autosaving an emptied EXISTING document so its saved state becomes
    // blank (fixes preview showing stale content after all text is deleted), but
    // don't auto-create a brand-new empty document from an untouched editor.
    if (!this.documentId && !this.editor.getText().trim()) return;
    if (html === this.lastSavedHtml && this.title === this.lastSavedTitle) return;
    this.performSave({ silent: true, skipSubscriptionCheck: true });
  }

  // Save (if needed) then render the compiled PDF into the right-hand preview
  // panel (PDF.js canvas — see getPreview()/loadPdfIntoViewer() below), not a modal.
  previewCurrentDoc() {
    if (!this.editor) return;

    // Empty documents are allowed: emptying a document then previewing must save
    // the now-blank body and render a blank page — not keep showing the stale,
    // previously saved content. So no "add some content" guard here anymore.
    const html = this.editor.getHTML();
    if (this.documentId && html === this.lastSavedHtml && this.title === this.lastSavedTitle) {
      this.getPreview();
      return;
    }

    this.performSave({ silent: true, onComplete: () => this.getPreview() });
  }

private performSave(opts: { silent: boolean; skipSubscriptionCheck?: boolean; onComplete?: () => void }) {
  if (!this.editor) return;
  if (this.isSaving) {
    if (!opts.silent) this.toast.info('A save is already in progress.');
    return;
  }
  if (!opts.skipSubscriptionCheck && !ensureActiveSubscription(this.confirmationDialogService)) return;

  const html = this.editor.getHTML();
  // Empty documents are allowed to save (and preview as a blank page); no
  // "add some content" guard here — see previewCurrentDoc()/autosaveTick().

  if (!this.documentname?.trim()) this.documentname = this.generateDefaultDocName();
  if (!this.author?.trim()) this.author = localStorage.getItem('name') || '';

  this.isSaving = true;
  this.saveStatus = 'saving';

  if (!this.documentId) {
    this.httpservice.sendPostLatexRequest(URLUtils.savedoc, { documentname: this.documentname.trim(), title: (this.title || '').trim() }).subscribe(
      (res: any) => {
        this.documentId = res.id;
        this.docSaved = true;
        this.lastSavedDocumentName = this.documentname.trim();
        this.processAndSave(html, opts.silent, opts.onComplete);
      },
      (err: HttpErrorResponse) => {
        this.isSaving = false;
        this.saveStatus = 'error';
        if (!opts.silent) this.toast.error(err.error?.message || 'Failed to create document.');
      }
    );
    return;
  }

  const trimmedName = this.documentname.trim();
  if (trimmedName !== this.lastSavedDocumentName) {
    const renameUrl = `${this.latexdoc}/v1/document/rename/${this.documentId}`;
    const token = localStorage.getItem('token') || '';
    const cofferId = localStorage.getItem('jid') || localStorage.getItem('cofferid') || '';
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'cofferid': cofferId };
    this.http.patch(renameUrl, { documentname: trimmedName }, { headers }).subscribe(
      () => { this.lastSavedDocumentName = trimmedName; this.processAndSave(html, opts.silent, opts.onComplete); },
      () => { this.processAndSave(html, opts.silent, opts.onComplete); }
    );
    return;
  }

  this.processAndSave(html, opts.silent, opts.onComplete);
}

  private renameDocUrl(docId: any): string {
    return URLUtils.renameDoc(docId);
  }

private processAndSave(html: string, silent: boolean = false, onComplete?: () => void) {
  // The Title block <h1 data-type="doc-title"> is persisted INLINE in the body
  // so the PDF renders it exactly where the user placed it — the backend's
  // html_to_latex converts it to centered large text in place (never a numbered
  // \section, never a relocated \maketitle). `title` is still sent for document
  // metadata / backwards-compatible listing, but no longer drives PDF layout.
  const body = html;
  const payload = { document: body, page: 1, title: (this.title || '').trim() };

  if (!this.pageId) {
    this.httpservice.sendPostLatexRequest(URLUtils.savedocID(this.documentId), payload).subscribe(
      (res: any) => {
        this.pageId = res.id;
        this.isSaving = false;
        this.saveStatus = 'saved';
        this.lastSavedHtml = html;
        this.lastSavedTitle = this.title;
        if (!silent) this.toast.success('Document saved successfully.');
        onComplete?.();
      },
      (err: HttpErrorResponse) => {
        this.isSaving = false;
        this.saveStatus = 'error';
        if (!silent) this.toast.error(err.error?.message || 'Save failed.');
      }
    );
  } else {
    this.httpservice.sendPatchLatexRequest(URLUtils.updateDoc(this.pageId), payload).subscribe(
      () => {
        this.isSaving = false;
        this.saveStatus = 'saved';
        this.lastSavedHtml = html;
        this.lastSavedTitle = this.title;
        if (!silent) this.toast.success('Document updated successfully.');
        onComplete?.();
      },
      (err: HttpErrorResponse) => {
        this.isSaving = false;
        this.saveStatus = 'error';
        if (!silent) this.toast.error(err.error?.message || 'Update failed.');
      }
    );
  }
}

private _saveLatex(latex: string, html: string, silent: boolean, onComplete?: () => void) {
  const latexHeader = `\\documentclass{article}\\usepackage{graphicx}\\usepackage{tabularx}\\usepackage{hyperref}\\usepackage{float}\\usepackage{geometry}\\geometry{a4paper,total={170mm,257mm},left=20mm,top=20mm,}<ltk>\\title{}<ltk>\\date{}<ltk>\\begin{document}<ltk>\\maketitle`;

  const blocks = latex.split('<ltk>').filter((b: string) => b.trim());
  const chunks: string[][] = [];
  for (let i = 0; i < blocks.length; i += 35) chunks.push(blocks.slice(i, i + 35));

  let page = 1;
  const saveChunk = (idx: number) => {
    if (idx >= chunks.length) {
      this.isSaving = false;
      this.saveStatus = 'saved';
      this.lastSavedHtml = html;
      onComplete?.();
      return;
    }

    const chunkLatex = chunks[idx].join('<ltk>');
    const documentText = (
      page === 1
        ? (latexHeader + '<ltk>' + chunkLatex)
        : chunkLatex
    ).replace(/\n/g, '<nln>');

    const payload = { document: documentText, page };

    if (!this.pageId) {
      this.httpservice.sendPostLatexRequest(URLUtils.savedocID(this.documentId), payload).subscribe(
        (res: any) => {
          this.pageId = res.id;
          if (!silent) this.toast.success('Document saved successfully.');
          page++;
          saveChunk(idx + 1);
        },
        (err: HttpErrorResponse) => {
          this.isSaving = false;
          this.saveStatus = 'error';
          if (!silent) this.toast.error(err.error?.message || 'Save failed.');
        }
      );
    } else {
      this.httpservice.sendPatchLatexRequest(URLUtils.updateDoc(this.pageId), payload).subscribe(
        () => {
          if (!silent) this.toast.success('Document updated successfully.');
          page++;
          saveChunk(idx + 1);
        },
        (err: HttpErrorResponse) => {
          this.isSaving = false;
          this.saveStatus = 'error';
          if (!silent) this.toast.error(err.error?.message || 'Update failed.');
        }
      );
    }
  };

  saveChunk(0);
}

  // ─── HTML → LaTeX ─────────────────────────────────────────────────────────

  htmlToLatex(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;
    const parts: string[] = [];

    const walkNode = (node: ChildNode): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return this.escapeLatex(node.textContent || '');
      }

      const el = node as unknown as Element;
      const tag = el.tagName?.toLowerCase();
      const children = () => Array.from(el.childNodes).map(walkNode).join('');

      switch (tag) {
        case 'h1': return `<ltk>\\section{${children()}}`;
        case 'h2': return `<ltk>\\subsection{${children()}}`;
        case 'h3': return `<ltk>\\subsubsection{${children()}}`;
        case 'p':  return `<ltk>\\paragraph{}${children()}`;
        case 'strong': return `\\textbf{${children()}}`;
        case 'em':     return `\\textit{${children()}}`;
        case 'u':      return `\\underline{${children()}}`;

        case 'ul': {
          const items = Array.from(el.querySelectorAll(':scope > li'))
            .map((li: Element) => `\\item ${this.escapeLatex(li.textContent || '')}`)
            .join('');
          return `<ltk>\\begin{itemize}${items}\\end{itemize}`;
        }
        case 'ol': {
          const items = Array.from(el.querySelectorAll(':scope > li'))
            .map((li: Element) => `\\item ${this.escapeLatex(li.textContent || '')}`)
            .join('');
          return `<ltk>\\begin{enumerate}${items}\\end{enumerate}`;
        }

        case 'table':
          return this.tableToLatex(el);

        case 'img': {
          const filename = el.getAttribute('data-filename') || el.getAttribute('src')?.split('/').pop() || '';
          const caption = el.getAttribute('alt') || '';
          const userId = localStorage.getItem('user_id');
          return `<ltk>\\begin{figure}[H] \\centering \\includegraphics[width=0.9\\textwidth,height=0.7\\textheight,keepaspectratio]{${this.latexImageFolder}/${userId}/${filename}} \\caption{${this.escapeLatex(caption)}} \\end{figure}`;
        }

        case 'div': {
          const dtype = el.getAttribute('data-type');
          if (dtype === 'overview') return `<ltk>\\abstract ${this.escapeLatex(el.textContent || '')}`;
          if (dtype === 'page-break') return `<ltk>\\newpage`;
          return children();
        }

        default:
          return children();
      }
    };

    Array.from(body.childNodes).forEach((n: ChildNode) => parts.push(walkNode(n)));
    return parts.join('').replace(/^<ltk>/, '');
  }

  private tableToLatex(table: Element): string {
    const rows = Array.from(table.querySelectorAll('tr'));
    if (!rows.length) return '';

    const colCount = rows[0].querySelectorAll('th, td').length;

    let keyString = '{<thsep>';
    for (let i = 0; i < colCount; i++) {
      keyString += 'X<thsep>';
    }
    keyString += '}\\hline ';

    let allTableData = '';
    rows.forEach((row: Element) => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      let rowData = cells.map(c => this.escapeLatex(c.textContent || '')).join('<csep>') + '<csep>';
      rowData = rowData.replace(/<csep>$/, '<rsep> \\hline ');
      allTableData += rowData.trim() + ' ';
    });

    const tableData = `${keyString} ${allTableData}`;
    return `\\begin{center}\\begin{tabularx}{1.0\\textwidth} ${tableData} \\end{tabularx} \\end{center}`;
  }

  private escapeLatex(text: string): string {
    return text
      .replace(/%/g, '\\%')
      .replace(/\$/g, '\\$')
      .replace(/#/g, '\\#')
      .replace(/~/g, '\\textasciitilde{}')
      .replace(/\^/g, '\\textasciicircum{}')
      .replace(/_/g, '\\_')
      .replace(/</g, '\\textless{}')
      .replace(/>/g, '\\textgreater{}');
  }

  // ─── Preview (PDF.js) ─────────────────────────────────────────────────────

  getPreview() {
    if (!this.documentId) {
      this.toast.error('Please save the document first.');
      return;
    }

    this.showPreviewDoc = false;
    this.spinnerService.show();

    const url = `${this.latexdoc}/v1/document/openview/${this.documentId}`;

    this.http.get(url, { responseType: 'arraybuffer' }).subscribe(
      async (pdfBytes: ArrayBuffer) => {
        try {
          await this.loadPdfIntoViewer(pdfBytes);
          this.spinnerService.hide();
        } catch (e) {
          console.error(e);
          this.spinnerService.hide();
          this.toast.error('Failed to render preview. Please try again.');
        }
      },
      (err: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load preview. Please try again.');
      }
    );
  }

  private async loadPdfIntoViewer(pdfBytes: ArrayBuffer): Promise<void> {
    this.teardownPdfPreview(true);

    const bytesCopy = pdfBytes.slice(0);
    this.pdfDoc = await (pdfjsLib as any).getDocument({ data: bytesCopy }).promise;

    this.pdfTotalPages = this.pdfDoc.numPages;
    this.pdfCurrentPage = 1;
    this.pdfZoom = 100;
    this.pdfThumbnails = [];

    const firstPage = await this.pdfDoc.getPage(1);
    const viewport = firstPage.getViewport({ scale: 1 });
    this.pdfPageWidthPt = viewport.width;
    this.pdfPageHeightPt = viewport.height;

    this.showPreviewDoc = true;
    await new Promise<void>((resolve) => setTimeout(resolve, 0));

    await this.renderCurrentPage();
    this.generateThumbnails();
  }

  private teardownPdfPreview(keepPanelOpenState = false) {
    if (this.pdfRenderTask) {
      try { this.pdfRenderTask.cancel(); } catch { /* no-op */ }
      this.pdfRenderTask = null;
    }
    if (this.pdfDoc) {
      try { this.pdfDoc.destroy(); } catch { /* no-op */ }
      this.pdfDoc = null;
    }
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
    this.showPreviewDoc = false;
    this.pdfCurrentPage = 1;
    this.pdfTotalPages = 1;
    this.pdfZoom = 100;
    this.pdfThumbnails = [];
    if (!keepPanelOpenState) {
      this.showThumbnailPanel = false;
    }
  }

  private async renderCurrentPage(): Promise<void> {
    if (!this.pdfDoc || !this.pdfCanvasEl || !this.previewCanvasScrollEl) return;

    if (this.pdfRenderTask) {
      try { this.pdfRenderTask.cancel(); } catch {}
      this.pdfRenderTask = null;
    }

    const page = await this.pdfDoc.getPage(this.pdfCurrentPage);
    const canvas = this.pdfCanvasEl.nativeElement;
    const scrollEl = this.previewCanvasScrollEl.nativeElement;

    const HORIZONTAL_PADDING = 32;
    const availableWidth = scrollEl.clientWidth - HORIZONTAL_PADDING;

    const targetWidth = Math.floor(availableWidth * (this.pdfZoom / 100));
    const scale = targetWidth / this.pdfPageWidthPt;
    const viewport = page.getViewport({ scale });

    const cssWidth  = Math.min(Math.floor(viewport.width), targetWidth);
    const cssHeight = Math.floor(viewport.height);

    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.floor(cssWidth * dpr);
    canvas.height = Math.floor(cssHeight * dpr);
    canvas.style.width  = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.style.maxWidth = '100%';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Size the page wrapper to the rendered canvas so the absolutely-positioned
    // annotation (link) layer overlays it exactly.
    const pageWrap = this.previewPageEl?.nativeElement;
    if (pageWrap) {
      pageWrap.style.width = `${cssWidth}px`;
      pageWrap.style.height = `${cssHeight}px`;
    }

    this.pdfRenderTask = page.render({ canvasContext: ctx, viewport });
    try {
      await this.pdfRenderTask.promise;
    } catch (e: any) {
      if (e?.name !== 'RenderingCancelledException') throw e;
      return; // render was superseded; skip the (now stale) annotation layer
    } finally {
      this.pdfRenderTask = null;
    }

    await this.renderAnnotationLayer(page, viewport, cssWidth, cssHeight, scale);
  }

  // Minimal pdf.js link service: the compiled document only has external URL
  // link annotations (\href from hyperref), so external-link binding is all that
  // is needed. Every link opens in a new tab.
  private readonly pdfLinkService = {
    externalLinkEnabled: true,
    externalLinkTarget: 2, // LinkTarget.BLANK
    externalLinkRel: 'noopener noreferrer',
    addLinkAttributes: (link: HTMLAnchorElement, url: string, _newWindow?: boolean) => {
      link.href = url;
      link.title = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    },
    getDestinationHash: () => '#',
    getAnchorUrl: (hash: string) => hash || '#',
    goToDestination: async () => {},
    goToPage: () => {},
    executeNamedAction: () => {},
    executeSetOCGState: () => {},
  };

  // Overlay clickable hyperlinks on the rendered page. pdf.js renders text/graphics
  // to the canvas but link annotations live in a separate DOM layer — without this
  // the PDF's links are visible but not clickable.
  private async renderAnnotationLayer(
    page: any, viewport: any, cssWidth: number, cssHeight: number, scale: number,
  ): Promise<void> {
    const layerDiv = this.annotationLayerEl?.nativeElement;
    if (!layerDiv) return;

    layerDiv.textContent = '';
    layerDiv.style.width = `${cssWidth}px`;
    layerDiv.style.height = `${cssHeight}px`;
    layerDiv.style.setProperty('--scale-factor', String(scale));
    layerDiv.style.setProperty('--total-scale-factor', String(scale));
    // pdf.js ≥6 sizes the layer via `round(..., var(--scale-round-x))`. Those
    // vars are defined in pdf.js' own viewer stylesheet, which this app does
    // not ship — leaving them undefined makes the whole width/height value
    // invalid, so the layer collapsed to 0×0 and every link annotation
    // (positioned in % of the layer) had zero hit area: links looked like
    // plain text and were unclickable in the preview.
    layerDiv.style.setProperty('--scale-round-x', '1px');
    layerDiv.style.setProperty('--scale-round-y', '1px');

    try {
      const annotations = await page.getAnnotations({ intent: 'display' });
      const hasLinks = Array.isArray(annotations) &&
        annotations.some((a: any) => a.subtype === 'Link');
      if (!hasLinks) return;

      // dontFlip matches how pdf.js' own viewer builds the annotation layer.
      const annViewport = viewport.clone({ dontFlip: true });
      const annotationLayer = new (pdfjsLib as any).AnnotationLayer({
        div: layerDiv,
        page,
        viewport: annViewport,
        linkService: this.pdfLinkService,
      });
      await annotationLayer.render({
        annotations,
        div: layerDiv,
        page,
        viewport: annViewport,
        linkService: this.pdfLinkService,
        renderForms: false,
      });
      // render() replaces the layer's width/height with pdf.js' var-based
      // round() expression; pin them back to the exact canvas dimensions so
      // the link overlay always matches the rendered page pixel-for-pixel.
      layerDiv.style.width = `${cssWidth}px`;
      layerDiv.style.height = `${cssHeight}px`;
    } catch (e) {
      // A stale page (user navigated/zoomed) can reject mid-render — non-fatal.
      console.warn('Annotation layer render skipped:', e);
    }
  }

  private async generateThumbnails(): Promise<void> {
    if (!this.pdfDoc || this.generatingThumbnails) return;
    this.generatingThumbnails = true;

    const THUMB_WIDTH = 96;

    try {
      const totalPages = this.pdfDoc.numPages;
      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        if (!this.pdfDoc) return;

        const page = await this.pdfDoc.getPage(pageNumber);
        const baseViewport = page.getViewport({ scale: 1 });
        const thumbScale = THUMB_WIDTH / baseViewport.width;
        const viewport = page.getViewport({ scale: thumbScale });

        const offscreen = document.createElement('canvas');
        offscreen.width = viewport.width;
        offscreen.height = viewport.height;
        const ctx = offscreen.getContext('2d')!;

        const renderTask = page.render({ canvasContext: ctx, viewport });
        await renderTask.promise;

        this.pdfThumbnails = [
          ...this.pdfThumbnails,
          { pageNumber, dataUrl: offscreen.toDataURL('image/png') },
        ];
      }
    } catch (e) {
      console.error('Thumbnail generation failed:', e);
    } finally {
      this.generatingThumbnails = false;
    }
  }

  toggleThumbnailPanel(): void {
    if (!this.showPreviewDoc) return;
    this.showThumbnailPanel = !this.showThumbnailPanel;
    setTimeout(() => { this.renderCurrentPage(); }, 0);
  }

  goToPageFromThumbnail(pageNumber: number): void {
    if (pageNumber === this.pdfCurrentPage) return;
    this.pdfCurrentPage = pageNumber;
    this.renderCurrentPage();
  }

  goToPdfPage(delta: number) {
    const next = this.pdfCurrentPage + delta;
    if (next < 1 || next > this.pdfTotalPages) return;
    this.pdfCurrentPage = next;
    this.renderCurrentPage();
  }

  startEditPdfPage() {
    if (this.pdfTotalPages <= 1) return;
    this.pdfPageEditing = true;
    setTimeout(() => {
      this.pdfPageInputEl?.nativeElement?.focus();
      this.pdfPageInputEl?.nativeElement?.select();
    }, 0);
  }

  commitPdfPageEdit(target: EventTarget | null) {
    this.pdfPageEditing = false;
    const input = target as HTMLInputElement | null;
    if (!input) return;
    let val = parseInt(input.value, 10);
    if (isNaN(val)) return;
    val = Math.min(Math.max(val, 1), this.pdfTotalPages);
    if (val === this.pdfCurrentPage) return;
    this.pdfCurrentPage = val;
    this.renderCurrentPage();
  }

  setPdfZoom(delta: number) {
    const next = Math.min(300, Math.max(50, this.pdfZoom + delta));
    if (next === this.pdfZoom) return;
    this.pdfZoom = next;
    this.renderCurrentPage();
  }

  // ─── Shared popover positioning ──────────────────────────────────────────
  // position:fixed + viewport clamping, reused by the table picker, table
  // hover controls, and link popover — simpler than CDK Overlay for anchors
  // that are either a stable toolbar button, a coordsAtPos()-derived rect, or
  // a per-mousemove-tick table rect (none of which need Overlay's connected-
  // position/scroll-strategy machinery).
  private computePopoverPos(
    anchor: { top: number; bottom: number; left: number },
    popoverW: number,
    popoverH: number,
  ): { top: number; left: number } {
    const margin = 6;
    let top = anchor.bottom + margin;
    if (top + popoverH > window.innerHeight - margin) {
      top = Math.max(margin, anchor.top - popoverH - margin);
    }
    const left = Math.min(Math.max(margin, anchor.left), window.innerWidth - popoverW - margin);
    return { top, left };
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    const target = event.target as Node;
    if (this.showTablePicker && !this.tablePickerEl?.nativeElement.contains(target)) this.closeTablePicker();
    if (this.showLinkPopover && !this.linkPopoverEl?.nativeElement.contains(target)) this.closeLinkPopover();
    this.preserveEditorFocus(event);
  }

  // Google-Docs-style focus retention: clicking the toolbar, the preview
  // panel, the sidebar, or any other non-editable chrome must NOT blur the
  // editor — the insertion caret keeps blinking (and the selection stays
  // rendered) exactly where it was. mousedown's default action is what moves
  // focus, so preventing it keeps focus in the editor while the click itself
  // (buttons, menus, PDF links) still fires normally. Genuine focus targets —
  // the editor body, inputs, textareas, other editables — are exempt, so the
  // caret only ever moves when the user clicks somewhere it belongs.
  private preserveEditorFocus(event: MouseEvent) {
    if (this.viewMode !== 'editor' || !this.editor) return;
    if (!this.editor.view?.hasFocus()) return;
    const target = event.target as HTMLElement;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest('.ProseMirror')) return; // caret placement / drag-select in the editor
    if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
    // Mat menus/overlays render inside a CDK overlay appended to <body>,
    // outside this component's DOM tree — let them manage their own focus and
    // clicks normally. If the editor ends up losing focus because of one of
    // these (e.g. keyboard-driven menu focus), scheduleFocusRestore() (wired
    // to the editor's onBlur callback) brings the caret right back once the
    // menu/popover closes.
    if (target.closest('.mat-mdc-menu-panel, .mat-menu-panel, .cdk-overlay-container, .tb-link-popover, .tb-grid-picker')) return;
    event.preventDefault();
  }

  // ─── Table grid picker ────────────────────────────────────────────────────

  openTablePicker() {
    if (!this.insertMenuBtnEl) return;
    this.closeLinkPopover();
    this.tablePickerGridRows = this.TABLE_PICKER_MIN_ROWS;
    this.tablePickerGridCols = this.TABLE_PICKER_MIN_COLS;
    this.tablePickerHoverRow = 0;
    this.tablePickerHoverCol = 0;
    this.tablePickerPos = this.computePopoverPos(this.insertMenuBtnEl.nativeElement.getBoundingClientRect(), 260, 220);
    this.showTablePicker = true;
  }

  onTablePickerHover(row: number, col: number) {
    this.tablePickerHoverRow = row;
    this.tablePickerHoverCol = col;
    if (row === this.tablePickerGridRows && this.tablePickerGridRows < this.TABLE_PICKER_MAX) this.tablePickerGridRows++;
    if (col === this.tablePickerGridCols && this.tablePickerGridCols < this.TABLE_PICKER_MAX) this.tablePickerGridCols++;
  }

  onTablePickerLeaveGrid() {
    this.tablePickerHoverRow = 0;
    this.tablePickerHoverCol = 0;
  }

  onTablePickerPick(row: number, col: number) {
    if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    // No forced header row: a header row is <th> with hard-coded bold that the
    // user can't turn off. Plain <td> cells let the user format the first row
    // (bold or not) like any other, and italic/bold set inside cells survives
    // to the PDF (a header row also got auto-bolded by the LaTeX converter).
    this.editor.chain().focus().insertTable({ rows: row, cols: col, withHeaderRow: false }).run();
    this.closeTablePicker();
  }

  closeTablePicker() {
    this.showTablePicker = false;
  }

  // Shared ceiling for every row/column addition path (picker and toolbar
  // +Row/+Col) — without this a table can be grown one column at a time with
  // no limit, which both looks wrong and breaks compiled-PDF rendering once a
  // table is wider than the page.
  private tableRowCount(table: HTMLTableElement): number { return table.rows.length; }
  private tableColCount(table: HTMLTableElement): number { return table.rows[0]?.cells.length ?? 0; }

  // ─── Link insertion/editing popover ──────────────────────────────────────

  private getSelectionAnchorRect(): { top: number; bottom: number; left: number } {
    const { from, to } = this.editor.state.selection;
    const start = this.editor.view.coordsAtPos(from);
    const end = this.editor.view.coordsAtPos(to);
    return {
      top: Math.min(start.top, end.top),
      bottom: Math.max(start.bottom, end.bottom),
      left: Math.min(start.left, end.left),
    };
  }

  private openLinkPopoverForSelection(mode: 'create' | 'edit') {
    if (!this.editor) return;
    this.closeTablePicker();
    if (mode === 'edit') this.editor.chain().extendMarkRange('link').run();
    const { from, to } = this.editor.state.selection;
    this.linkPopoverRange = { from, to };
    this.linkPopoverMode = mode;
    this.linkPopoverUrl = this.editor.getAttributes('link')?.['href'] || '';
    const popoverWidth = mode === 'edit' ? 336 : 300; // edit mode adds the "open link" button
    this.linkPopoverPos = this.computePopoverPos(this.getSelectionAnchorRect(), popoverWidth, 44);
    this.showLinkPopover = true;
    setTimeout(() => this.linkUrlInputEl?.nativeElement?.focus(), 0);
  }

  /** Normalizes a raw popover URL into a href TipTap's Link mark will accept. */
  private normalizeLinkHref(raw: string): string {
    // Already has an explicit scheme, either "scheme://…" (http, https, ftp…)
    // or a bare "scheme:" one that never takes slashes (mailto:, tel:).
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) || /^(mailto|tel):/i.test(raw)) {
      return raw;
    }
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      return `mailto:${raw}`;
    }
    return `https://${raw}`;
  }

  /** Opens the link currently shown in the popover in a new tab ("follow" a link). */
  openLinkPopoverUrl() {
    const raw = this.linkPopoverUrl.trim();
    if (!raw) return;
    window.open(this.normalizeLinkHref(raw), '_blank', 'noopener,noreferrer');
  }

  applyLinkPopover() {
    if (!this.editor || !this.linkPopoverRange) return;
    const raw = this.linkPopoverUrl.trim();
    if (!raw) { this.removeLinkPopover(); return; }
    const href = this.normalizeLinkHref(raw);
    const applied = this.editor.chain().focus()
      .setTextSelection(this.linkPopoverRange)
      .extendMarkRange('link')
      .setLink({ href })
      .run();
    if (!applied) {
      // setLink() silently returns false for disallowed URL schemes
      // (isAllowedUri) instead of throwing — surface that to the user.
      this.toast.error('That URL is not allowed. Use an http(s) or mailto link.');
      return;
    }
    this.closeLinkPopover();
  }

  removeLinkPopover() {
    if (!this.editor || !this.linkPopoverRange) { this.closeLinkPopover(); return; }
    this.editor.chain().focus()
      .setTextSelection(this.linkPopoverRange)
      .extendMarkRange('link')
      .unsetLink()
      .run();
    this.closeLinkPopover();
  }

  closeLinkPopover() {
    this.showLinkPopover = false;
    this.linkPopoverRange = null;
  }

  // ─── Open Document ────────────────────────────────────────────────────────
openFile(docid: any) {
  this.spinnerService.show();
  this.httpservice.sendGetLatexRequest(URLUtils.opendocID(docid)).subscribe(
    (req: any) => {
      this.spinnerService.hide();
      if (!req?.length) return;
      req.sort((a: any, b: any) => a.page - b.page);
      this.pageId = req[0]?.pageid;

      // restore the document title (separate from documentname) for the title input
      this.title = req[0]?.title ?? '';

      // document field now stores HTML directly (Title block stripped out on
      // save). Fall back to a doc-title still embedded in older documents so
      // their title survives the upgrade.
      const html = req[0]?.document || '<p></p>';
      if (!this.title.trim()) {
        const legacy = html.match(/<h1[^>]*\bdata-type=["']doc-title["'][^>]*>([\s\S]*?)<\/h1>/i);
        if (legacy) this.title = legacy[1].replace(/<[^>]+>/g, '').trim();
      }

      // Re-insert the Title block at the top from the `title` field so it shows
      // in the editor exactly as it renders (centered) in the preview.
      this.editor.commands.setContent(this.buildEditorHtml(html, this.title));
      this.lastSavedHtml = this.editor.getHTML();
      this.lastSavedTitle = this.title;
      this.docSaved = true;
      // Loading content resets selection to the very start of the doc, which
      // is exactly where the blinking caret should be for an opened document
      // — re-focus here since initEditor()'s earlier focus() call ran before
      // this content existed.
      setTimeout(() => this.editor?.commands.focus('start', { scrollIntoView: false }), 0);
      this.getPreview();
    },
    (err: HttpErrorResponse) => {
      this.spinnerService.hide();
      this.toast.error(err.error?.msg || 'Failed to open document.');
    }
  );
}

  private extractTitleAuthor(latex: string) {
    const titleMatch = latex.match(/\\title\s*\{([^}]*)\}/);
    this.title = titleMatch?.[1] ?? '';
    const authorMatch = latex.match(/\\author\{([^}]*)\}/);
    this.author = authorMatch?.[1] ?? '';
  }

  latexToHtml(latex: string): string {
    let s = latex.replace(/<nln>/g, '\n').replace(/<ltk>/g, '\n').replace(/<thsep>/g, '');
    s = s.replace(/^[\s\S]*?\\begin\{document\}\s*\\maketitle\s*/m, '');

    s = s.replace(/\\abstract\s+([^\n\\]+)/g, (_m: string, c: string) =>
      `<div data-type="overview">${c.trim()}</div>`);

    s = s.replace(/\\subsubsection\{([^}]*)\}([^\n\\]*)/g, (_m: string, t: string, c: string) => `<h3>${t}${c.trim()}</h3>`);
    s = s.replace(/\\subsection\{([^}]*)\}([^\n\\]*)/g, (_m: string, t: string, c: string) => `<h2>${t}${c.trim()}</h2>`);
    s = s.replace(/\\section\{([^}]*)\}([^\n\\]*)/g, (_m: string, t: string, c: string) => `<h1>${t}${c.trim()}</h1>`);
    s = s.replace(/\\paragraph\{([^}]*)\}([^\n\\]*)/g, (_m: string, _t: string, c: string) => `<p>${c.trim()}</p>`);

    s = s.replace(/\\begin\{itemize\}([\s\S]*?)\\end\{itemize\}/g, (_m: string, inner: string) => {
      const items = (inner.match(/\\item\s+([^\n\\]*)/g) || [])
        .map((i: string) => `<li>${i.replace(/\\item\s+/, '').trim()}</li>`).join('');
      return `<ul>${items}</ul>`;
    });
    s = s.replace(/\\begin\{enumerate\}([\s\S]*?)\\end\{enumerate\}/g, (_m: string, inner: string) => {
      const items = (inner.match(/\\item\s+([^\n\\]*)/g) || [])
        .map((i: string) => `<li>${i.replace(/\\item\s+/, '').trim()}</li>`).join('');
      return `<ol>${items}</ol>`;
    });

    s = s.replace(
      /\\begin\{center\}\\begin\{tabularx\}\{[^}]+\}\s*\{[^}]*\}\\hline\s*([\s\S]*?)\\end\{tabularx\}\s*\\end\{center\}/g,
      (_m: string, tableBody: string) => {
        const rows = tableBody.split(/\\hline/).map(r => r.trim()).filter(r => r.length > 0);
        if (rows.length === 0) return '';

        let html = '<table><tbody>';
        rows.forEach((row: string, rowIdx: number) => {
          const cleanedRow = row.replace(/<rsep>\s*$/, '').trim();
          html += '<tr>';
          const cells = cleanedRow.split('<csep>').map((c: string) => c.trim());
          cells.forEach((cell: string) => {
            const tag = rowIdx === 0 ? 'th' : 'td';
            html += `<${tag}>${cell}</${tag}>`;
          });
          html += '</tr>';
        });
        html += '</tbody></table>';
        return html;
      }
    );

    s = s.replace(/\\begin\{figure\}\[H\]\s*\\centering\s*\\includegraphics\[[^\]]*\]\{([^}]+)\}\s*\\caption\{(.*?)\}\s*\\end\{figure\}/g,
      (_m: string, path: string, caption: string) => {
        const filename = path.split('/').pop();
        const userId = localStorage.getItem('user_id');
        const src = `${this.latexImageFolder}/${userId}/${filename}`;
        return `<img src="${src}" data-filename="${filename}" alt="${caption}" />`;
      });

    s = s.replace(/\\newpage/g, '<div data-type="page-break"></div>');

    s = s.replace(/\\textbf\{([^}]*)\}/g, '<strong>$1</strong>');
    s = s.replace(/\\textit\{([^}]*)\}/g, '<em>$1</em>');
    s = s.replace(/\\underline\{([^}]*)\}/g, '<u>$1</u>');

    s = s.replace(/\\%/g, '%').replace(/\\\$/g, '$').replace(/\\#/g, '#')
      .replace(/\\_/g, '_').replace(/\\textless\{\}/g, '<').replace(/\\textgreater\{\}/g, '>');

    return s.trim() || '<p></p>';
  }

  // ─── UI Helpers ───────────────────────────────────────────────────────────

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') event.preventDefault();
  }

  restrictFirstPosition(event: any) {
    const v: string = event.target.value;
    if (/^[0-9]/.test(v)) { event.target.value = v.substring(1); this.toast.error('Numbers should not be first character'); }
  }

  restrictSpaces(event: any) {
    event.target.value = event.target.value.replace(/\s{2,}/g, ' ');
  }

  restrictFirstCharacter(event: any) {
    const v: string = event.target.value;
    if (/^[-_]/.test(v)) { event.target.value = v.substring(1); this.toast.error('Hyphens (-) and underscore (_) should not be the first character'); }
  }

  @HostListener('document:mouseleave')
  onMouseLeave() {
    this.menuTrigger1?.closeMenu(); this.menuTrigger3?.closeMenu();
    this.closeTablePicker(); this.closeLinkPopover();
  }

  @HostListener('document:scroll')
  onScroll() {
    this.menuTrigger1?.closeMenu(); this.menuTrigger3?.closeMenu();
    this.closeTablePicker(); this.closeLinkPopover();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (!this.showPreviewDoc) return;
    this.renderCurrentPage();
  }
}

// ─── Dialog Sub-Components ────────────────────────────────────────────────────

@Component({
    selector: 'app-opendialog-box',
    templateUrl: './opendialog-box.component.html',
    standalone: false
})
export class OpendialogBoxComponent implements OnInit {
  documents: any[] = [];
  searchText = '';

  constructor(
    public dialogRef: MatDialogRef<OpendialogBoxComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private httpservice: HttpService,
    private spinnerService: NgxSpinnerService,
    private toast: ToastrService,
  ) {}

  ngOnInit() {
    this.loadDocuments();
  }

  loadDocuments() {
    this.spinnerService.show();
    this.httpservice.sendGetLatexRequest(URLUtils.savedoc).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        this.documents = res || [];
      },
      (err: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load documents.');
      }
    );
  }

  ondocumentClick(docid: any, documentname: string) {
    this.dialogRef.close({ docid, docname: documentname });
  }

  closeDialog() {
    this.dialogRef.close();
  }
}

@Component({
    selector: 'app-download-box',
    templateUrl: './download-box.component.html',
    standalone: false
})
export class DownloadBoxComponent implements OnInit {
  mydForm!: FormGroup;
  submitted = false;

  constructor(
    public dialogRef: MatDialogRef<DownloadBoxComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private httpservice: HttpService,
    private spinnerService: NgxSpinnerService,
    private toast: ToastrService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.mydForm = this.fb.group({
      documentname: ['', Validators.required],
    });
  }

  downloadDoc() {
    this.submitted = true;
    if (this.mydForm.invalid) return;

    this.spinnerService.show();
    this.httpservice.sendGetLatexRequest(URLUtils.getPreview(this.data.documentId)).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        this.toast.success('Downloaded successfully.');
        this.dialogRef.close();
      },
      (err: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.toast.error(err.error?.message || 'Download failed.');
      }
    );
  }

  closeDialog() {
    this.dialogRef.close();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') event.preventDefault();
  }

  restrictFirstPosition(event: any) {
    const v: string = event.target.value;
    if (/^[0-9]/.test(v)) { event.target.value = v.substring(1); }
  }

  restrictSpaces(event: any) {
    event.target.value = event.target.value.replace(/\s{2,}/g, ' ');
  }

  restrictFirstCharacter(event: any) {
    const v: string = event.target.value;
    if (/^[-_]/.test(v)) { event.target.value = v.substring(1); }
  }
}

@Component({
    selector: 'app-saveas-box',
    templateUrl: './saveas-box.component.html',
    standalone: false
})
export class SaveasBoxComponent implements OnInit {
  mydForm!: FormGroup;
  submitted = false;

  constructor(
    public dialogRef: MatDialogRef<SaveasBoxComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private httpservice: HttpService,
    private spinnerService: NgxSpinnerService,
    private toast: ToastrService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.mydForm = this.fb.group({
      documentname: ['', Validators.required],
    });
  }

  downloadDoc() {
    this.submitted = true;
    if (this.mydForm.invalid) return;

    const fname = this.mydForm.value.documentname?.trim();
    this.spinnerService.show();
    this.httpservice.sendPostLatexRequest(URLUtils.savedoc, { documentname: fname }).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        this.toast.success('Saved successfully.');
        this.dialogRef.close(res);
      },
      (err: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.toast.error(err.error?.message || 'Save failed.');
      }
    );
  }

  closeDialog() {
    this.dialogRef.close();
  }
    
}